import { Link, router, usePage } from '@inertiajs/react';
import { useState, useEffect, useCallback, useMemo } from 'react';
import echo from '@/lib/echo';
import type { HexData } from '@/components/HexMap';
import { EditorRenderer } from '@/components/Editor';
import { HexResources, type ResourceData } from '@/components/HexResources';
import { HexQuickLinks } from '@/components/HexQuickLinks';
import {
    ItemDetailModal,
    filterVaultItems,
    getDefaultFilters,
    parseFiltersFromUrl,
    updateUrlWithFilters,
    type AssignableCharacter,
    type VaultFiltersState,
} from '@/components/Vault';
import { FloatingNavButtons } from '@/components/FloatingTomeIcon';
import { HexSettingsDialog } from '@/components/HexSettingsDialog';
import { HexLibrarySidebar, type HexLibrary, type HexLibraryBook, type HexLibraryChapter } from '@/components/HexLibrary';
import {
    NewPageModal,
    RelinquishHexModal,
    BookModal,
    ChapterModal,
    ResourcesCollectedModal,
} from '@/components/Hex/Modals';
import {
    SubHexMapTab,
    LibraryTab,
    BuildingsTab,
    CharactersTab,
    VaultTab,
    ManageHexTab,
    AdventuresTab,
    AttemptsTab,
} from '@/components/Hex/Tabs';
import type { Item } from '@/types/items';
import type { Adventure, AdventureAttempt, GrantedReward, AvailableCharacter } from '@/types/adventures';
import { formatDuration } from '@/types/adventures';
import HexLayout, { type SidebarContext } from '@/layouts/HexLayout';

/** Represents a page within a hex */
interface HexPage {
    id: number;
    name: string;
    content?: string | null;
    order: number;
}

/** Pagination link from Laravel */
interface PaginationLink {
    url: string | null;
    label: string;
    active: boolean;
}

/** Paginated attempts response */
interface PaginatedAttempts {
    data: AdventureAttempt[];
    links: PaginationLink[];
    last_page: number;
}

interface Props {
    hex: HexData & {
        /** Legacy markdown content (deprecated, use pages instead) */
        content: string | null;
        collaborators: Array<{ id: number; username: string }>;
        /** Hex resource production per hour */
        resources: ResourceData;
        /** Whether this is an enclave hex (has reputation system) */
        isEnclave?: boolean;
    };
    /** All pages for sidebar navigation (legacy, kept for backwards compatibility) */
    pages: HexPage[];
    /** Currently selected page with full content */
    currentPage: HexPage | null;
    /** Hierarchical library structure (books -> chapters -> pages) */
    library: HexLibrary;
    canEdit: boolean;
    /** Whether the current user is the hex owner (can relinquish) */
    isOwner: boolean;
    /** Map of hex names to their IDs for resolving cross-hex links */
    hexNameToId: Record<string, number>;
    /** Whether resources can be collected from this hex (owner only) */
    canCollect: boolean;
    /** Pending resources available for collection (owner only) */
    pendingResources: ResourceData | null;
    /** ISO timestamp of next available collection time */
    nextCollectionAt: string | null;
    /** Hours since last collection */
    hoursSinceCollection: number;
    /** User's total accumulated resources */
    userResources: ResourceData | null;
    /** Count of completed buildings (level >= 1) for Buildings tab visibility */
    completedBuildingsCount: number;
    /** Whether user has claimed at least one hex (required for character creation) */
    hasClaimedHex: boolean;
    /** Unassigned items in the user's vault (owner only) */
    vaultItems: Item[];
    /** Characters available for item assignment (owner only) */
    assignableCharacters: AssignableCharacter[];
    /** Available adventures for the Adventures tab */
    adventures: Adventure[];
    /** User's adventure attempts for the Attempts tab */
    adventureAttempts: PaginatedAttempts;
    /** Count of in-progress adventure attempts */
    inProgressCount: number;
    /** Count of attempts ready to complete */
    readyToCompleteCount: number;
    /** Stat labels for adventure challenges */
    statLabels: Record<string, string>;
    /** Skill labels for adventure challenges */
    skillLabels: Record<string, string>;
    /** Specific adventure attempt for the attempt-detail tab (when viewing a single attempt) */
    attemptDetail: AdventureAttempt | null;
    /** Specific adventure for the adventure-detail tab (when viewing a single adventure) */
    adventureDetail: Adventure | null;
    /** Available characters for adventure-detail tab (character selection) */
    availableCharacters: AvailableCharacter[];
    /** Whether the current user is a GM */
    isGm: boolean;
    /** Whether the current user has blocked the hex owner */
    isOwnerBlocked?: boolean;
    /** Reputation data for enclave hexes (authenticated users only) */
    reputation?: ReputationData | null;
}

/** Tab type for the hex view */
type HexViewTab = 'submap' | 'library' | 'buildings' | 'characters' | 'vault' | 'manage' | 'adventures' | 'attempts' | 'attempt-detail' | 'adventure-detail' | 'reputation';

/** Reputation data for enclave hexes */
interface ReputationData {
    id: number;
    value: number;
    level: string;
    progress: number;
    enclaveName: string | null;
    hexId: number;
    hexName: string;
}

/** Building bonus data from collection */
interface BuildingBonus {
    level: number;
    level_bonus: number;
    assigned_count: number;
    character_bonus: number;
    total_bonus: number;
}

/** Flash data from collection with all building bonuses */
interface CollectedFlashData {
    resources: ResourceData;
    userResources: ResourceData;
    nextCollectionAt: string;
    hoursCollected: number;
    baseResources: ResourceData;
    lumberMillBonus: BuildingBonus | null;
    herbalistHutBonus: BuildingBonus | null;
    teaHouseBonus: BuildingBonus | null;
    shrineBonus: BuildingBonus | null;
    smokehouseBonus: BuildingBonus | null;
}

/**
 * Get the initial tab from URL query parameter.
 * Falls back to 'submap' if not specified or invalid (submap is the default tab).
 */
function getInitialTab(): HexViewTab {
    if (typeof window === 'undefined') return 'submap';

    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');

    if (tab === 'library') return 'library';
    if (tab === 'manage') return 'manage';
    if (tab === 'vault') return 'vault';
    if (tab === 'characters') return 'characters';
    if (tab === 'buildings') return 'buildings';
    if (tab === 'adventures') return 'adventures';
    if (tab === 'attempts') return 'attempts';
    if (tab === 'attempt-detail') return 'attempt-detail';
    if (tab === 'adventure-detail') return 'adventure-detail';
    if (tab === 'reputation') return 'reputation';
    return 'submap';
}

/**
 * Hex page view - displays hex details and multi-page content.
 * Features a left sidebar for page navigation.
 */
export default function HexView({
    hex,
    pages,
    currentPage,
    library,
    canEdit,
    isOwner,
    hexNameToId,
    canCollect: initialCanCollect,
    pendingResources: initialPendingResources,
    nextCollectionAt: initialNextCollectionAt,
    // hoursSinceCollection is used for initial display but timer updates it
    hoursSinceCollection: _initialHoursSinceCollection,
    userResources: initialUserResources,
    completedBuildingsCount,
    hasClaimedHex,
    vaultItems,
    assignableCharacters,
    adventures,
    adventureAttempts,
    inProgressCount,
    readyToCompleteCount,
    statLabels,
    skillLabels,
    attemptDetail,
    adventureDetail,
    availableCharacters,
    isGm,
    isOwnerBlocked: initialIsOwnerBlocked = false,
    reputation,
}: Props) {
    // Suppress unused variable warning - hoursSinceCollection is passed for initial state
    void _initialHoursSinceCollection;
    // Suppress unused variable warning - completedBuildingsCount is passed but Buildings tab is always visible now
    void completedBuildingsCount;

    // Tab state - "library", "buildings", "characters", "vault", or "manage"
    // Initialize from URL query parameter if present
    const [activeTab, setActiveTab] = useState<HexViewTab>(() =>
        getInitialTab()
    );

    const [showNewPageModal, setShowNewPageModal] = useState(false);
    const [newPageName, setNewPageName] = useState('');
    const [isCreatingPage, setIsCreatingPage] = useState(false);
    // State for new page's chapter (for library structure)
    const [newPageChapterId, setNewPageChapterId] = useState<number | null>(null);

    // State for book modal
    const [showBookModal, setShowBookModal] = useState(false);
    const [editingBook, setEditingBook] = useState<HexLibraryBook | null>(null);
    const [bookTitle, setBookTitle] = useState('');
    const [bookDescription, setBookDescription] = useState('');
    const [bookHue, setBookHue] = useState(260);
    const [isSavingBook, setIsSavingBook] = useState(false);

    // State for chapter modal
    const [showChapterModal, setShowChapterModal] = useState(false);
    const [editingChapter, setEditingChapter] = useState<HexLibraryChapter | null>(null);
    const [editingChapterBookId, setEditingChapterBookId] = useState<number | null>(null);
    const [chapterTitle, setChapterTitle] = useState('');
    const [isSavingChapter, setIsSavingChapter] = useState(false);

    // State for relinquish confirmation modal
    const [showRelinquishModal, setShowRelinquishModal] = useState(false);
    const [isRelinquishing, setIsRelinquishing] = useState(false);

    // State for vault item detail modal
    const [selectedVaultItem, setSelectedVaultItem] = useState<Item | null>(null);

    // State for vault filters - initialize from URL if on vault tab
    const [vaultFilters, setVaultFilters] = useState<VaultFiltersState>(() => {
        if (typeof window !== 'undefined' && window.location.search.includes('tab=vault')) {
            return parseFiltersFromUrl();
        }
        return getDefaultFilters();
    });

    // Filtered vault items based on current filters
    const filteredVaultItems = useMemo(
        () => filterVaultItems(vaultItems, vaultFilters),
        [vaultItems, vaultFilters]
    );

    // Handle vault filter changes - update state and URL
    const handleVaultFiltersChange = useCallback((newFilters: VaultFiltersState) => {
        setVaultFilters(newFilters);
        updateUrlWithFilters(newFilters);
    }, []);

    // State for blocking the hex owner
    const [isOwnerBlocked, setIsOwnerBlocked] = useState(initialIsOwnerBlocked);
    const [isBlockingOwner, setIsBlockingOwner] = useState(false);

    // State for GM hex settings dialog
    const [showSettingsDialog, setShowSettingsDialog] = useState(false);
    const [hexName, setHexName] = useState(hex.name);
    const [hexOwnerName, setHexOwnerName] = useState(hex.ownerName || null);

    // Note: sidebar state is now managed by HexLayout

    /**
     * Handle tab change - updates state and URL query parameter.
     * Uses pushState to add to browser history so back/forward buttons work.
     */
    const handleTabChange = useCallback((tab: HexViewTab) => {
        setActiveTab(tab);

        // Update URL with new tab parameter
        const url = new URL(window.location.href);
        if (tab === 'submap') {
            // Remove tab param for submap (default)
            url.searchParams.delete('tab');
        } else {
            url.searchParams.set('tab', tab);
        }
        // Use pushState to add to browser history (enables back/forward navigation)
        window.history.pushState({ tab }, '', url.toString());
    }, []);

    // Handle browser back/forward navigation - update tab state when URL changes
    useEffect(() => {
        const handlePopState = () => {
            // Re-read the tab from the URL when user navigates with back/forward
            const newTab = getInitialTab();
            setActiveTab(newTab);
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    // Resource collection state
    const [isCollecting, setIsCollecting] = useState(false);
    const [canCollect, setCanCollect] = useState(initialCanCollect);
    const [pendingResources, setPendingResources] = useState(initialPendingResources);
    const [userResources, setUserResources] = useState(initialUserResources);
    const [nextCollectionAt, setNextCollectionAt] = useState(initialNextCollectionAt);
    const [minutesUntilCollection, setMinutesUntilCollection] = useState<number | null>(null);
    const [showCollectedModal, setShowCollectedModal] = useState(false);
    const [collectedResources, setCollectedResources] = useState<ResourceData | null>(null);
    const [collectedHours, setCollectedHours] = useState<number>(0);
    const [collectedBaseResources, setCollectedBaseResources] = useState<ResourceData | null>(null);
    const [showBreakdown, setShowBreakdown] = useState(false);
    // Building bonus states
    const [lumberMillBonus, setLumberMillBonus] = useState<BuildingBonus | null>(null);
    const [herbalistHutBonus, setHerbalistHutBonus] = useState<BuildingBonus | null>(null);
    const [teaHouseBonus, setTeaHouseBonus] = useState<BuildingBonus | null>(null);
    const [shrineBonus, setShrineBonus] = useState<BuildingBonus | null>(null);
    const [smokehouseBonus, setSmokehouseBonus] = useState<BuildingBonus | null>(null);

    // State for live attempt detail updates via websocket
    const [liveAttemptDetail, setLiveAttemptDetail] = useState<AdventureAttempt | null>(attemptDetail);

    // Sync attemptDetail state when prop changes (e.g., navigation to different attempt)
    useEffect(() => {
        setLiveAttemptDetail(attemptDetail);
    }, [attemptDetail]);

    // Subscribe to websocket channel for real-time stage completion updates
    useEffect(() => {
        if (!liveAttemptDetail?.id) return;

        const channel = echo.private(`adventure-attempt.${liveAttemptDetail.id}`);

        channel.listen('AdventureStageCompleted', (event: {
            attempt: AdventureAttempt;
            stage_attempt: AdventureAttempt['stage_attempts'][0];
            message: string;
        }) => {
            // Update the attempt detail with fresh data from the server
            setLiveAttemptDetail(event.attempt);
        });

        return () => {
            channel.stopListening('AdventureStageCompleted');
            echo.leave(`adventure-attempt.${liveAttemptDetail.id}`);
        };
    }, [liveAttemptDetail?.id]);

    // Sync userResources state with props when they change (e.g., after building construction)
    // This ensures the UI updates when resources are deducted for building costs
    useEffect(() => {
        setUserResources(initialUserResources);
    }, [initialUserResources]);

    // Sync pendingResources and canCollect state with props when they change
    // This ensures the UI updates after collection (server sends zeros)
    useEffect(() => {
        setPendingResources(initialPendingResources);
    }, [initialPendingResources]);

    useEffect(() => {
        setCanCollect(initialCanCollect);
    }, [initialCanCollect]);

    // Sync nextCollectionAt state with props when they change
    // This ensures the timer uses the correct value after page reload
    useEffect(() => {
        setNextCollectionAt(initialNextCollectionAt);
    }, [initialNextCollectionAt]);

    // Get flash data for successful collection
    const { props } = usePage<{ flash?: { collected?: CollectedFlashData } }>();
    const collectedFlash = props.flash?.collected;

    // Handle flash data from successful collection
    useEffect(() => {
        if (collectedFlash) {
            setCollectedResources(collectedFlash.resources);
            setUserResources(collectedFlash.userResources);
            setNextCollectionAt(collectedFlash.nextCollectionAt);
            setCollectedHours(collectedFlash.hoursCollected);
            setCollectedBaseResources(collectedFlash.baseResources);
            // Store building bonuses
            setLumberMillBonus(collectedFlash.lumberMillBonus);
            setHerbalistHutBonus(collectedFlash.herbalistHutBonus);
            setTeaHouseBonus(collectedFlash.teaHouseBonus);
            setShrineBonus(collectedFlash.shrineBonus);
            setSmokehouseBonus(collectedFlash.smokehouseBonus);
            setCanCollect(false);
            setPendingResources({ tea: 0, wood: 0, mystical: 0, medicinal: 0, edible: 0 });
            setShowBreakdown(false); // Reset breakdown state for new collection
            setShowCollectedModal(true);
        }
    }, [collectedFlash]);

    // Calculate minutes until next collection and update every minute
    useEffect(() => {
        if (!isOwner || !nextCollectionAt) return;

        const updateTimer = () => {
            const next = new Date(nextCollectionAt);
            const now = new Date();
            const diffMs = next.getTime() - now.getTime();
            const diffMins = Math.ceil(diffMs / 60000);

            if (diffMins <= 0) {
                // Collection is now available
                setMinutesUntilCollection(null);
                setCanCollect(true);
                // Estimate pending resources based on time passed
                // This is approximate - server will calculate exact amount
                const hoursPassed = Math.floor(-diffMins / 60) + 1;
                setPendingResources({
                    tea: hex.resources.tea * hoursPassed,
                    wood: hex.resources.wood * hoursPassed,
                    mystical: hex.resources.mystical * hoursPassed,
                    medicinal: hex.resources.medicinal * hoursPassed,
                    edible: hex.resources.edible * hoursPassed,
                });
            } else {
                setMinutesUntilCollection(diffMins);
                setCanCollect(false);
            }
        };

        // Update immediately and then every minute
        updateTimer();
        const interval = setInterval(updateTimer, 60000);

        return () => clearInterval(interval);
    }, [nextCollectionAt, isOwner, hex.resources]);

    // Handle resource collection
    const handleCollect = useCallback(() => {
        if (isCollecting || !canCollect) return;

        setIsCollecting(true);
        router.post(`/hex/${hex.id}/collect`, {}, {
            preserveScroll: true,
            onFinish: () => {
                setIsCollecting(false);
            },
        });
    }, [hex.id, isCollecting, canCollect]);

    // Handle creating a new page
    const handleCreatePage = (e: React.FormEvent) => {
        e.preventDefault();
        if (!newPageName.trim() || isCreatingPage || !newPageChapterId) return;

        setIsCreatingPage(true);
        // Use the correct route: POST /hex-pages/{hexId}
        router.post(`/hex-pages/${hex.id}`, {
            name: newPageName.trim(),
            content: '',
            chapter_id: newPageChapterId,
        }, {
            onSuccess: () => {
                setShowNewPageModal(false);
                setNewPageName('');
                setNewPageChapterId(null);
            },
            onFinish: () => {
                setIsCreatingPage(false);
            },
        });
    };

    // Handle deleting a page
    const handleDeletePage = (pageId: number) => {
        if (!confirm('Are you sure you want to delete this page? This cannot be undone.')) {
            return;
        }
        router.delete(`/hex/${hex.id}/pages/${pageId}`);
    };

    // Handle relinquishing the hex (giving up ownership)
    const handleRelinquish = () => {
        if (isRelinquishing) return;

        setIsRelinquishing(true);
        router.delete(`/hex/${hex.id}/relinquish`, {
            onFinish: () => {
                setIsRelinquishing(false);
                setShowRelinquishModal(false);
            },
        });
    };

    // Handle blocking/unblocking the hex owner
    const handleToggleBlockOwner = useCallback(() => {
        if (isBlockingOwner || !hex.owner) return;

        setIsBlockingOwner(true);

        if (isOwnerBlocked) {
            // Unblock the owner
            router.delete(`/missives/blocks/${hex.owner.id}`, {
                preserveScroll: true,
                onSuccess: () => {
                    setIsOwnerBlocked(false);
                },
                onFinish: () => {
                    setIsBlockingOwner(false);
                },
            });
        } else {
            // Block the owner
            router.post('/missives/blocks', {
                blocked_username: hex.owner.username,
            }, {
                preserveScroll: true,
                onSuccess: () => {
                    setIsOwnerBlocked(true);
                },
                onFinish: () => {
                    setIsBlockingOwner(false);
                },
            });
        }
    }, [hex.owner, isBlockingOwner, isOwnerBlocked]);

    // Handle saving hex settings (GM only)
    const handleSaveSettings = useCallback((name: string, ownerName: string | null) => {
        setHexName(name);
        setHexOwnerName(ownerName);
    }, []);

    // ===== Book/Chapter/Page modal handlers =====

    // Open new book modal
    const handleShowNewBookModal = useCallback(() => {
        setEditingBook(null);
        setBookTitle('');
        setBookDescription('');
        setBookHue(260);
        setShowBookModal(true);
    }, []);

    // Open edit book modal
    const handleShowEditBookModal = useCallback((book: HexLibraryBook) => {
        setEditingBook(book);
        setBookTitle(book.title);
        setBookDescription(book.description || '');
        setBookHue(book.hue);
        setShowBookModal(true);
    }, []);

    // Save book (create or update)
    const handleSaveBook = useCallback((e: React.FormEvent) => {
        e.preventDefault();
        if (!bookTitle.trim() || isSavingBook) return;

        setIsSavingBook(true);

        if (editingBook) {
            // Update existing book
            router.patch(`/hex/${hex.id}/books/${editingBook.id}`, {
                title: bookTitle.trim(),
                description: bookDescription.trim() || null,
                hue: bookHue,
            }, {
                onSuccess: () => {
                    setShowBookModal(false);
                    setEditingBook(null);
                },
                onFinish: () => setIsSavingBook(false),
            });
        } else {
            // Create new book
            router.post(`/hex/${hex.id}/books`, {
                title: bookTitle.trim(),
                description: bookDescription.trim() || null,
                hue: bookHue,
            }, {
                onSuccess: () => {
                    setShowBookModal(false);
                },
                onFinish: () => setIsSavingBook(false),
            });
        }
    }, [hex.id, bookTitle, bookDescription, bookHue, editingBook, isSavingBook]);

    // Open new chapter modal
    const handleShowNewChapterModal = useCallback((bookId: number) => {
        setEditingChapter(null);
        setEditingChapterBookId(bookId);
        setChapterTitle('');
        setShowChapterModal(true);
    }, []);

    // Open edit chapter modal
    const handleShowEditChapterModal = useCallback((chapter: HexLibraryChapter, bookId: number) => {
        setEditingChapter(chapter);
        setEditingChapterBookId(bookId);
        setChapterTitle(chapter.title);
        setShowChapterModal(true);
    }, []);

    // Save chapter (create or update)
    const handleSaveChapter = useCallback((e: React.FormEvent) => {
        e.preventDefault();
        if (!chapterTitle.trim() || isSavingChapter || !editingChapterBookId) return;

        setIsSavingChapter(true);

        if (editingChapter) {
            // Update existing chapter
            router.patch(`/hex/${hex.id}/books/${editingChapterBookId}/chapters/${editingChapter.id}`, {
                title: chapterTitle.trim(),
            }, {
                onSuccess: () => {
                    setShowChapterModal(false);
                    setEditingChapter(null);
                    setEditingChapterBookId(null);
                },
                onFinish: () => setIsSavingChapter(false),
            });
        } else {
            // Create new chapter
            router.post(`/hex/${hex.id}/books/${editingChapterBookId}/chapters`, {
                title: chapterTitle.trim(),
            }, {
                onSuccess: () => {
                    setShowChapterModal(false);
                    setEditingChapterBookId(null);
                },
                onFinish: () => setIsSavingChapter(false),
            });
        }
    }, [hex.id, chapterTitle, editingChapter, editingChapterBookId, isSavingChapter]);

    // Open new page modal for a specific chapter
    const handleShowNewPageModal = useCallback((chapterId: number) => {
        setNewPageChapterId(chapterId);
        setNewPageName('');
        setShowNewPageModal(true);
    }, []);

    return (
        <HexLayout
            title={hexName}
            hex={{ ...hex, name: hexName, ownerName: hexOwnerName }}
            isOwner={isOwner}
            sidebar={({ closeSidebar, isMobile }: SidebarContext) => (
                <>
                        {/* Quick Navigation Links - Collapsible */}
                        <HexQuickLinks
                            hexId={hex.id}
                            isOwner={isOwner}
                            isOpenHex={hex.isOpenHex}
                            isEnclave={hex.isEnclave}
                            activeItem={activeTab}
                            collapsible
                            onTabChange={handleTabChange}
                            closeSidebar={closeSidebar}
                            isMobile={isMobile}
                            vaultCount={vaultItems.length}
                            inProgressCount={inProgressCount}
                        />

                        {/* GM Settings section - only visible to GMs - styled as secret steel panel */}
                        {isGm && (
                            <div className="mx-3 my-4">
                                <div
                                    className="relative rounded-lg overflow-hidden"
                                    style={{
                                        background: 'linear-gradient(145deg, #4A4A4A 0%, #3A3A3A 30%, #2A2A2A 70%, #1A1A1A 100%)',
                                        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 2px 4px rgba(0,0,0,0.4)',
                                        border: '2px solid #5A5A5A',
                                    }}
                                >
                                    {/* Brushed metal texture */}
                                    <div
                                        className="absolute inset-0 opacity-20 pointer-events-none"
                                        style={{
                                            backgroundImage: 'repeating-linear-gradient(90deg, transparent 0px, transparent 1px, rgba(255,255,255,0.03) 1px, rgba(255,255,255,0.03) 2px)',
                                        }}
                                    />

                                    {/* Panel header with warning stripes */}
                                    <div
                                        className="relative px-3 py-2"
                                        style={{
                                            background: 'linear-gradient(180deg, rgba(90,90,90,0.4) 0%, rgba(60,60,60,0.2) 100%)',
                                            borderBottom: '1px solid rgba(90,90,90,0.4)',
                                        }}
                                    >
                                        {/* Diagonal warning stripes */}
                                        <div
                                            className="absolute right-0 top-0 bottom-0 w-8 opacity-30"
                                            style={{
                                                backgroundImage: 'repeating-linear-gradient(45deg, #D97706, #D97706 4px, transparent 4px, transparent 8px)',
                                            }}
                                        />
                                        <h2
                                            className="relative text-xs font-bold uppercase tracking-wider flex items-center gap-1.5"
                                            style={{ color: '#C0C0C0' }}
                                        >
                                            <span style={{ color: '#F59E0B' }}>⚙</span>
                                            GM Controls
                                        </h2>
                                    </div>

                                    {/* Button content */}
                                    <div className="relative p-3">
                                        <button
                                            onClick={() => setShowSettingsDialog(true)}
                                            className="w-full px-3 py-2 text-sm font-bold rounded flex items-center justify-center gap-2 cursor-pointer transition-all"
                                            style={{
                                                background: 'linear-gradient(180deg, #6A6A6A 0%, #4A4A4A 50%, #3A3A3A 100%)',
                                                border: '2px solid #7A7A7A',
                                                color: '#E8E8E8',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.2)',
                                            }}
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            </svg>
                                            Settings
                                        </button>
                                    </div>

                                    {/* Corner rivets */}
                                    <div
                                        className="absolute top-1.5 left-1.5 w-2 h-2 rounded-full"
                                        style={{ background: 'radial-gradient(circle at 30% 30%, #8A8A8A, #4A4A4A)' }}
                                    />
                                    <div
                                        className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full"
                                        style={{ background: 'radial-gradient(circle at 30% 30%, #8A8A8A, #4A4A4A)' }}
                                    />
                                    <div
                                        className="absolute bottom-1.5 left-1.5 w-2 h-2 rounded-full"
                                        style={{ background: 'radial-gradient(circle at 30% 30%, #8A8A8A, #4A4A4A)' }}
                                    />
                                    <div
                                        className="absolute bottom-1.5 right-1.5 w-2 h-2 rounded-full"
                                        style={{ background: 'radial-gradient(circle at 30% 30%, #8A8A8A, #4A4A4A)' }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Hex Owner section - only show for non-owners */}
                        {!isOwner && hex.owner && (
                            <div className="p-4 border-t border-slate-700">
                                <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
                                    Hex Owner
                                </h2>
                                <div className="flex flex-col gap-2">
                                    <span className="text-white font-medium">{hexOwnerName || hex.owner.username}</span>
                                    <div className="flex items-center gap-2">
                                        {/* Send Missive button - only show if not blocked */}
                                        {!isOwnerBlocked && (
                                            <Link
                                                href={`/missives/create?to=${encodeURIComponent(hex.owner.username)}`}
                                                className="px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-1"
                                                title={`Send a missive to ${hex.owner.username}`}
                                            >
                                                ✉️ Message
                                            </Link>
                                        )}
                                        {/* Block/Unblock button */}
                                        <button
                                            onClick={handleToggleBlockOwner}
                                            disabled={isBlockingOwner}
                                            className={`px-3 py-1.5 text-xs rounded-lg transition-colors flex items-center gap-1 ${
                                                isOwnerBlocked
                                                    ? 'bg-green-600 hover:bg-green-700 text-white'
                                                    : 'bg-red-600/20 hover:bg-red-600/40 text-red-400 border border-red-600/50'
                                            } ${isBlockingOwner ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            title={isOwnerBlocked ? `Unblock ${hex.owner.username}` : `Block ${hex.owner.username}`}
                                        >
                                            {isBlockingOwner ? '...' : isOwnerBlocked ? '✓ Unblock' : '🚫 Block'}
                                        </button>
                                    </div>
                                    {/* Blocked status message */}
                                    {isOwnerBlocked && (
                                        <p className="text-xs text-red-400 mt-1">
                                            You have blocked this user. They cannot send you missives.
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Library sidebar - only show on Library tab */}
                        {activeTab === 'library' && (
                            <HexLibrarySidebar
                                library={library}
                                currentPageId={currentPage?.id ?? null}
                                hexId={hex.id}
                                canEdit={canEdit}
                                isMobile={isMobile}
                                closeSidebar={closeSidebar}
                                onShowNewBookModal={handleShowNewBookModal}
                                onShowNewChapterModal={handleShowNewChapterModal}
                                onShowNewPageModal={handleShowNewPageModal}
                                onShowEditBookModal={handleShowEditBookModal}
                                onShowEditChapterModal={handleShowEditChapterModal}
                            />
                        )}

                        {/* Resource Collection Section (owner only, hidden for open hexes) */}
                        {isOwner && !hex.isOpenHex && (
                            <div className="p-4 border-t border-slate-700">
                                <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
                                    Resource Collection
                                </h2>

                                {/*/!* Pending resources to collect - only show if there are non-zero resources *!/*/}
                                {/*{pendingResources && (pendingResources.tea > 0 || pendingResources.wood > 0 || pendingResources.mystical > 0 || pendingResources.medicinal > 0 || pendingResources.edible > 0) && (*/}
                                {/*    <div className="mb-3">*/}
                                {/*        <p className="text-xs text-gray-400 mb-2">Pending resources:</p>*/}
                                {/*        <HexResources resources={pendingResources} compact showHeaders />*/}
                                {/*    </div>*/}
                                {/*)}*/}

                                {/* Collection button when resources are available */}
                                {canCollect && (
                                    <button
                                        onClick={handleCollect}
                                        disabled={isCollecting}
                                        className="w-full px-3 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-800 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors mb-3"
                                    >
                                        {isCollecting ? 'Collecting...' : 'Collect Resources'}
                                    </button>
                                )}

                                {/* Timer showing when next collection is available - always visible when not collecting */}
                                {!canCollect && (
                                    <div className="text-center">
                                        <p className="text-xs text-gray-400 mb-1">Next collection in:</p>
                                        <p className="text-lg font-semibold text-yellow-400">
                                            {minutesUntilCollection !== null
                                                ? `${minutesUntilCollection} min${minutesUntilCollection !== 1 ? 's' : ''}`
                                                : 'Calculating...'}
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* User's Total Resources (owner only, hidden for open hexes) */}
                        {isOwner && userResources && !hex.isOpenHex && (
                            <div className="p-4 border-t border-slate-700">
                                <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
                                    Your Resources
                                </h2>
                                <HexResources resources={userResources} compact showHeaders />
                            </div>
                        )}
                </>
            )}
        >
            {/* Main content area */}
            <div className="p-4 md:p-8">
                        {/* Sub-Hex Map Tab Content */}
                        {activeTab === 'submap' && (
                            <SubHexMapTab hexId={hex.id} isOwner={isOwner} />
                        )}

                        {/* Library Tab Content */}
                        {activeTab === 'library' && (
                            <LibraryTab
                                currentPage={currentPage}
                                canEdit={canEdit}
                                hexId={hex.id}
                                hexNameToId={hexNameToId}
                                onShowNewPageModal={() => setShowNewPageModal(true)}
                            />
                        )}

                        {/* Buildings Tab Content - hidden for open hexes */}
                        {activeTab === 'buildings' && !hex.isOpenHex && (
                            <BuildingsTab hexId={hex.id} isOwner={isOwner} />
                        )}

                        {/* Characters Tab Content - hidden for open hexes */}
                        {activeTab === 'characters' && !hex.isOpenHex && (
                            <CharactersTab hexId={hex.id} isOwner={isOwner} hasClaimedHex={hasClaimedHex} isGm={isGm} />
                        )}

                        {/* Vault Tab Content (owner only) - hidden for open hexes */}
                        {activeTab === 'vault' && isOwner && !hex.isOpenHex && (
                            <VaultTab
                                vaultItems={vaultItems}
                                filteredVaultItems={filteredVaultItems}
                                vaultFilters={vaultFilters}
                                onFiltersChange={handleVaultFiltersChange}
                                onItemClick={setSelectedVaultItem}
                            />
                        )}

                        {/* Manage Tab Content (owner only) */}
                        {activeTab === 'manage' && (
                            <ManageHexTab
                                isOwner={isOwner}
                                hexQ={hex.q}
                                hexR={hex.r}
                                terrain={hex.terrain}
                                ownerUsername={hex.owner?.username ?? null}
                                collaborators={hex.collaborators}
                                resources={hex.resources}
                                onShowRelinquishModal={() => setShowRelinquishModal(true)}
                            />
                        )}

                        {/* Adventures Tab Content - hidden for open hexes */}
                        {activeTab === 'adventures' && !hex.isOpenHex && (
                            <AdventuresTab
                                adventures={adventures}
                                hexId={hex.id}
                                inProgressCount={inProgressCount}
                                statLabels={statLabels}
                                skillLabels={skillLabels}
                                onTabChange={handleTabChange}
                            />
                        )}

                        {/* Adventure Results Tab Content - hidden for open hexes */}
                        {activeTab === 'attempts' && !hex.isOpenHex && (
                            <AttemptsTab
                                adventureAttempts={adventureAttempts}
                                hexId={hex.id}
                                readyToCompleteCount={readyToCompleteCount}
                                onTabChange={handleTabChange}
                            />
                        )}

                        {/* Attempt Detail Tab Content */}
                        {activeTab === 'attempt-detail' && liveAttemptDetail && (
                            <AttemptDetailContent
                                attempt={liveAttemptDetail}
                                hexId={hex.id}
                                statLabels={statLabels}
                                skillLabels={skillLabels}
                                isGm={isGm}
                            />
                        )}

                        {/* Attempt Detail Tab - No attempt found */}
                        {activeTab === 'attempt-detail' && !liveAttemptDetail && (
                            <div className="text-center py-12">
                                <div className="text-6xl mb-4">🔍</div>
                                <h2 className="text-xl font-semibold text-white mb-2">Attempt Not Found</h2>
                                <p className="text-gray-400 mb-6">
                                    The adventure attempt you're looking for doesn't exist or you don't have access to it.
                                </p>
                                <button
                                    onClick={() => handleTabChange('attempts')}
                                    className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                                >
                                    View My Adventures
                                </button>
                            </div>
                        )}

                        {/* Adventure Detail Tab Content */}
                        {activeTab === 'adventure-detail' && adventureDetail && (
                            <AdventureDetailContent
                                adventure={adventureDetail}
                                characters={availableCharacters}
                                hexId={hex.id}
                                statLabels={statLabels}
                                skillLabels={skillLabels}
                            />
                        )}

                        {/* Adventure Detail Tab - No adventure found */}
                        {activeTab === 'adventure-detail' && !adventureDetail && (
                            <div className="text-center py-12">
                                <div className="text-6xl mb-4">🔍</div>
                                <h2 className="text-xl font-semibold text-white mb-2">Adventure Not Found</h2>
                                <p className="text-gray-400 mb-6">
                                    The adventure you're looking for doesn't exist or is not currently available.
                                </p>
                                <button
                                    onClick={() => handleTabChange('adventures')}
                                    className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                                >
                                    Browse Adventures
                                </button>
                            </div>
                        )}

                        {/* Reputation Tab Content (enclave hexes only) */}
                        {activeTab === 'reputation' && hex.isEnclave && (
                            <ReputationTab reputation={reputation} hexName={hex.name} />
                        )}

                {/* New Page Modal */}
                <NewPageModal
                    isOpen={showNewPageModal}
                    onClose={() => {
                        setShowNewPageModal(false);
                        setNewPageName('');
                    }}
                    onSubmit={handleCreatePage}
                    pageName={newPageName}
                    setPageName={setNewPageName}
                    isCreating={isCreatingPage}
                />

                {/* Relinquish Confirmation Modal */}
                <RelinquishHexModal
                    isOpen={showRelinquishModal}
                    onClose={() => setShowRelinquishModal(false)}
                    onConfirm={handleRelinquish}
                    hexName={hex.name}
                    isRelinquishing={isRelinquishing}
                />

                {/* Book Modal (Create/Edit) */}
                <BookModal
                    isOpen={showBookModal}
                    onClose={() => {
                        setShowBookModal(false);
                        setEditingBook(null);
                    }}
                    onSubmit={handleSaveBook}
                    editingBook={editingBook}
                    bookTitle={bookTitle}
                    setBookTitle={setBookTitle}
                    bookDescription={bookDescription}
                    setBookDescription={setBookDescription}
                    bookHue={bookHue}
                    setBookHue={setBookHue}
                    isSaving={isSavingBook}
                />

                {/* Chapter Modal (Create/Edit) */}
                <ChapterModal
                    isOpen={showChapterModal}
                    onClose={() => {
                        setShowChapterModal(false);
                        setEditingChapter(null);
                        setEditingChapterBookId(null);
                    }}
                    onSubmit={handleSaveChapter}
                    editingChapter={editingChapter}
                    chapterTitle={chapterTitle}
                    setChapterTitle={setChapterTitle}
                    isSaving={isSavingChapter}
                />

                {/* Resources Collected Success Modal */}
                <ResourcesCollectedModal
                    isOpen={showCollectedModal && collectedResources !== null}
                    onClose={() => setShowCollectedModal(false)}
                    collectedResources={collectedResources!}
                    collectedHours={collectedHours}
                    collectedBaseResources={collectedBaseResources}
                    showBreakdown={showBreakdown}
                    setShowBreakdown={setShowBreakdown}
                    lumberMillBonus={lumberMillBonus}
                    herbalistHutBonus={herbalistHutBonus}
                    teaHouseBonus={teaHouseBonus}
                    shrineBonus={shrineBonus}
                    smokehouseBonus={smokehouseBonus}
                />

                {/* Vault Item Detail Modal */}
                {selectedVaultItem && (
                    <ItemDetailModal
                        item={selectedVaultItem}
                        characters={assignableCharacters}
                        onClose={() => setSelectedVaultItem(null)}
                        showRecycle={true}
                    />
                )}

                {/* Mobile bottom navigation - hidden on hex page (only shows on map page) */}
                <FloatingNavButtons userHex={isOwner ? hex : null} isMapPage={false} />

                {/* GM Hex Settings Dialog */}
                {isGm && (
                    <HexSettingsDialog
                        hexId={hex.id}
                        hexName={hexName}
                        ownerName={hexOwnerName}
                        isOpen={showSettingsDialog}
                        onClose={() => setShowSettingsDialog(false)}
                        onSave={handleSaveSettings}
                    />
                )}
            </div>
        </HexLayout>
    );
}

/**
 * Decorative corner flourish SVG component for parchment borders.
 * Rotated via className to create all four corners.
 */
function PaperCornerFlourish({ className = '' }: { className?: string }) {
    return (
        <svg
            className={`w-8 h-8 text-amber-800/30 ${className}`}
            viewBox="0 0 100 100"
            fill="currentColor"
        >
            <path d="M0,0 Q50,10 50,50 Q10,50 0,0 M10,0 Q40,20 40,40 Q20,40 10,0" />
        </svg>
    );
}

/**
 * Reusable paper card component with parchment aesthetic.
 * Used for adventure attempt detail sections.
 */
function PaperCard({
    children,
    className = '',
    rotation = 0,
    variant = 'default',
}: {
    children: React.ReactNode;
    className?: string;
    rotation?: number;
    variant?: 'default' | 'aged' | 'torn';
}) {
    const getVariantStyles = () => {
        switch (variant) {
            case 'aged':
                return {
                    background: 'linear-gradient(135deg, #e8d5b7 0%, #d4c4a8 25%, #e0d2bc 50%, #d9c9a5 75%, #e5d8c0 100%)',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.3), inset 0 0 40px rgba(139,90,43,0.2)',
                };
            case 'torn':
                return {
                    background: 'linear-gradient(145deg, #f0e4d1 0%, #e5d6be 30%, #efe2cf 60%, #e8d9c2 100%)',
                    boxShadow: '0 3px 12px rgba(0,0,0,0.25), inset 0 0 30px rgba(139,90,43,0.1)',
                };
            default:
                return {
                    background: 'linear-gradient(135deg, #f5e6d3 0%, #e8d5b7 25%, #f0e2cc 50%, #e5d4b8 75%, #f2e4c9 100%)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.3), inset 0 0 60px rgba(139,90,43,0.15)',
                };
        }
    };

    return (
        <div
            className={`relative rounded-lg overflow-hidden ${className}`}
            style={{
                ...getVariantStyles(),
                transform: rotation !== 0 ? `rotate(${rotation}deg)` : undefined,
            }}
        >
            {/* Decorative corner flourishes */}
            <PaperCornerFlourish className="absolute top-1 left-1" />
            <PaperCornerFlourish className="absolute top-1 right-1 rotate-90" />
            <PaperCornerFlourish className="absolute bottom-1 left-1 -rotate-90" />
            <PaperCornerFlourish className="absolute bottom-1 right-1 rotate-180" />

            {/* Aged paper edge effect - top */}
            <div
                className="absolute top-0 left-0 right-0 h-2"
                style={{
                    background: 'linear-gradient(to bottom, rgba(139,90,43,0.15), transparent)',
                }}
            />

            {/* Aged paper edge effect - bottom */}
            <div
                className="absolute bottom-0 left-0 right-0 h-2"
                style={{
                    background: 'linear-gradient(to top, rgba(139,90,43,0.15), transparent)',
                }}
            />

            {/* Inner content */}
            <div className="relative p-6">{children}</div>
        </div>
    );
}

/**
 * Single nixie tube digit display.
 */
function NixieDigit({ digit, glowing = true }: { digit: string; glowing?: boolean }) {
    return (
        <div
            className="relative w-8 h-12 flex items-center justify-center"
            style={{
                background: 'linear-gradient(180deg, #1a1a1a 0%, #0d0d0d 100%)',
                borderRadius: '4px',
                boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.8), inset 0 -1px 2px rgba(255,255,255,0.1)',
            }}
        >
            {/* Glass tube effect */}
            <div
                className="absolute inset-0.5 rounded"
                style={{
                    background: 'linear-gradient(180deg, rgba(255,150,50,0.03) 0%, transparent 50%)',
                    border: '1px solid rgba(255,255,255,0.05)',
                }}
            />
            {/* Digit */}
            <span
                className="relative text-2xl"
                style={{
                    color: glowing ? '#ff6b1a' : '#331500',
                    textShadow: glowing
                        ? '0 0 10px #ff6b1a, 0 0 20px #ff4500, 0 0 30px #ff4500, 0 0 40px #cc3300'
                        : 'none',
                    fontFamily: '"Nixie One", cursive',
                }}
            >
                {digit}
            </span>
            {/* Warm glow reflection on glass */}
            {glowing && (
                <div
                    className="absolute inset-0 rounded"
                    style={{
                        background: 'radial-gradient(ellipse at center, rgba(255,100,20,0.15) 0%, transparent 70%)',
                    }}
                />
            )}
        </div>
    );
}

/**
 * Nixie tube colon separator.
 */
function NixieColon({ glowing = true }: { glowing?: boolean }) {
    return (
        <div className="flex flex-col items-center justify-center gap-1.5 px-0.5">
            <div
                className="w-1.5 h-1.5 rounded-full"
                style={{
                    background: glowing ? '#ff6b1a' : '#331500',
                    boxShadow: glowing ? '0 0 6px #ff6b1a, 0 0 12px #ff4500' : 'none',
                }}
            />
            <div
                className="w-1.5 h-1.5 rounded-full"
                style={{
                    background: glowing ? '#ff6b1a' : '#331500',
                    boxShadow: glowing ? '0 0 6px #ff6b1a, 0 0 12px #ff4500' : 'none',
                }}
            />
        </div>
    );
}

/**
 * Nixie tube time display.
 */
function NixieTubeDisplay({
    seconds,
    label,
    isComplete = false,
}: {
    seconds: number;
    label: string;
    isComplete?: boolean;
}) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    // Format digits
    const showHours = hours > 0;
    const h1 = Math.floor(hours / 10).toString();
    const h2 = (hours % 10).toString();
    const m1 = Math.floor(minutes / 10).toString();
    const m2 = (minutes % 10).toString();
    const s1 = Math.floor(secs / 10).toString();
    const s2 = (secs % 10).toString();

    const glowing = !isComplete && seconds > 0;

    return (
        <div className="flex flex-col items-center">
            {/* Label plate */}
            <div
                className="px-3 py-1 mb-2 rounded text-xs font-serif uppercase tracking-wider"
                style={{
                    background: 'linear-gradient(180deg, #c4a574 0%, #a08050 100%)',
                    color: '#2d1f0f',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3), 0 1px 2px rgba(0,0,0,0.3)',
                    border: '1px solid #8b6914',
                }}
            >
                {label}
            </div>
            {/* Nixie tubes */}
            <div className="flex items-center gap-0.5">
                {showHours && (
                    <>
                        <NixieDigit digit={h1} glowing={glowing} />
                        <NixieDigit digit={h2} glowing={glowing} />
                        <NixieColon glowing={glowing} />
                    </>
                )}
                <NixieDigit digit={m1} glowing={glowing} />
                <NixieDigit digit={m2} glowing={glowing} />
                <NixieColon glowing={glowing} />
                <NixieDigit digit={s1} glowing={glowing} />
                <NixieDigit digit={s2} glowing={glowing} />
            </div>
            {/* Status indicator */}
            {isComplete && (
                <div className="mt-2 text-xs font-serif text-green-400 uppercase tracking-wider">
                    Complete
                </div>
            )}
        </div>
    );
}

/**
 * Wooden box containing nixie tube timers.
 */
function NixieTubeTimerBox({
    currentStageSeconds,
    totalSeconds,
    isMultiStage,
    currentStage,
    totalStages,
    isComplete = false,
}: {
    currentStageSeconds: number;
    totalSeconds: number;
    isMultiStage: boolean;
    currentStage: number;
    totalStages: number;
    isComplete?: boolean;
}) {
    return (
        <div
            className="rounded-lg overflow-hidden"
            style={{
                // Wooden box exterior
                background: 'linear-gradient(180deg, #5c3d1e 0%, #4a3118 20%, #3d280f 80%, #2d1f0a 100%)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)',
                border: '2px solid #2a1a08',
            }}
        >
            {/* Wood grain texture overlay */}
            <div
                className="absolute inset-0 opacity-10 pointer-events-none"
                style={{
                    backgroundImage: `repeating-linear-gradient(
                        90deg,
                        transparent 0px,
                        transparent 2px,
                        rgba(0,0,0,0.1) 2px,
                        rgba(0,0,0,0.1) 4px
                    )`,
                }}
            />

            {/* Inner panel with displays */}
            <div
                className="m-2 p-4 rounded"
                style={{
                    background: 'linear-gradient(180deg, #1f1f1f 0%, #141414 100%)',
                    boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.8), 0 1px 0 rgba(255,255,255,0.05)',
                    border: '1px solid #0a0a0a',
                }}
            >
                {/* Brass corner accents */}
                <div className="absolute top-3 left-3 w-2 h-2 rounded-full" style={{ background: 'linear-gradient(135deg, #d4a84b 0%, #8b6914 100%)' }} />
                <div className="absolute top-3 right-3 w-2 h-2 rounded-full" style={{ background: 'linear-gradient(135deg, #d4a84b 0%, #8b6914 100%)' }} />
                <div className="absolute bottom-3 left-3 w-2 h-2 rounded-full" style={{ background: 'linear-gradient(135deg, #d4a84b 0%, #8b6914 100%)' }} />
                <div className="absolute bottom-3 right-3 w-2 h-2 rounded-full" style={{ background: 'linear-gradient(135deg, #d4a84b 0%, #8b6914 100%)' }} />

                <div className={`flex ${isMultiStage ? 'justify-around' : 'justify-center'} items-start gap-8`}>
                    {/* Current Stage Timer (multi-stage only) */}
                    {isMultiStage && (
                        <NixieTubeDisplay
                            seconds={currentStageSeconds}
                            label={`Stage ${currentStage} of ${totalStages}`}
                            isComplete={isComplete || currentStageSeconds <= 0}
                        />
                    )}

                    {/* Total Adventure Timer */}
                    <NixieTubeDisplay
                        seconds={totalSeconds}
                        label={isMultiStage ? 'Total Expedition' : 'Time Remaining'}
                        isComplete={isComplete}
                    />
                </div>
            </div>
        </div>
    );
}

/**
 * Paper scrap timer component with hand-drawn progress aesthetic.
 */
function PaperScrapTimer({
    remainingSeconds,
    totalSeconds,
    isReady,
}: {
    remainingSeconds: number;
    totalSeconds: number;
    isReady: boolean;
}) {
    const progress = Math.max(0, Math.min(100, 100 - (remainingSeconds / totalSeconds) * 100));

    // Format time
    const hours = Math.floor(remainingSeconds / 3600);
    const minutes = Math.floor((remainingSeconds % 3600) / 60);
    const secs = remainingSeconds % 60;

    const timeString = hours > 0
        ? `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
        : `${minutes}:${secs.toString().padStart(2, '0')}`;

    return (
        <div
            className="relative rounded-md overflow-hidden"
            style={{
                background: 'linear-gradient(145deg, #e8dcc8 0%, #d9ccb4 50%, #e5d8c4 100%)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2), inset 0 0 20px rgba(139,90,43,0.1)',
            }}
        >
            {/* Torn edge effect - top */}
            <div
                className="absolute top-0 left-0 right-0 h-1"
                style={{
                    background: 'repeating-linear-gradient(90deg, transparent 0px, transparent 4px, rgba(139,90,43,0.2) 4px, rgba(139,90,43,0.2) 8px)',
                }}
            />

            {/* Torn edge effect - bottom */}
            <div
                className="absolute bottom-0 left-0 right-0 h-1"
                style={{
                    background: 'repeating-linear-gradient(90deg, rgba(139,90,43,0.2) 0px, rgba(139,90,43,0.2) 4px, transparent 4px, transparent 8px)',
                }}
            />

            <div className="p-4">
                {/* Time display - handwritten style */}
                <div className="text-center mb-3">
                    <span
                        className="text-3xl font-serif italic text-amber-900"
                        style={{ fontFamily: 'Georgia, serif' }}
                    >
                        {isReady ? 'Complete!' : timeString}
                    </span>
                    {!isReady && (
                        <div className="text-xs text-amber-800/60 font-serif italic mt-1">
                            until your characters return
                        </div>
                    )}
                </div>

                {/* Hand-drawn progress line */}
                <div className="relative h-6 mt-2">
                    {/* Paper texture base */}
                    <div
                        className="absolute inset-0 rounded"
                        style={{
                            background: 'linear-gradient(to right, #d4c4a8, #e0d2bc, #d8c8ac)',
                        }}
                    />

                    {/* Progress track (like pencil marks) */}
                    <svg
                        className="absolute inset-0 w-full h-full"
                        viewBox="0 0 100 24"
                        preserveAspectRatio="none"
                    >
                        {/* Background sketchy line - full width guide */}
                        <path
                            d="M 5 12 Q 25 9, 50 14 T 95 11"
                            fill="none"
                            stroke="rgba(139,90,43,0.2)"
                            strokeWidth="2"
                            strokeLinecap="round"
                        />

                        {/* Progress line (hand-drawn ink style) */}
                        <path
                            d={`M 5 12 Q ${5 + progress * 0.2} ${9 + Math.sin(progress / 10) * 3}, ${5 + progress * 0.45} ${14 - Math.cos(progress / 15) * 3} T ${5 + progress * 0.9} ${11 + Math.sin(progress / 8) * 2}`}
                            fill="none"
                            stroke="#5c3d1e"
                            strokeWidth="2"
                            strokeLinecap="round"
                        />
                    </svg>

                    {/* Start and end markers */}
                    <div className="absolute left-1 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-amber-800/40" />
                    <div
                        className={`absolute right-1 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full ${
                            isReady ? 'bg-green-700' : 'bg-amber-800/40'
                        }`}
                    />
                </div>

                {/* Journey markers */}
                <div className="flex justify-between mt-1 text-xs font-serif text-amber-800/50 italic px-1">
                    <span>Departed</span>
                    <span>{isReady ? 'Arrived!' : 'Destination'}</span>
                </div>
            </div>
        </div>
    );
}

/**
 * AttemptDetailContent - Displays adventure attempt details within the hex layout.
 * Two-paper design: parchment for story, official form for mechanics.
 */
function AttemptDetailContent({
    attempt,
    hexId,
    statLabels,
    skillLabels,
    isGm,
}: {
    attempt: AdventureAttempt;
    hexId: number;
    statLabels: Record<string, string>;
    skillLabels: Record<string, string>;
    isGm: boolean;
}) {
    const isInProgress = attempt.status.value === 'in_progress';
    const isCompleted = !isInProgress && attempt.status.value !== 'pending';
    const isSuccess = attempt.outcome?.value === 'success' || attempt.outcome?.value === 'critical_success';
    const isPartial = attempt.outcome?.value === 'partial_success';
    const isMultiStage = attempt.is_multi_stage;

    // Get the appropriate dialogue for completed single-stage attempts
    const outcomeDialogue = !isMultiStage && isCompleted
        ? (isSuccess || isPartial ? attempt.adventure?.dialogue_success : attempt.adventure?.dialogue_failure)
        : null;

    // Get completed stage attempts for multi-stage adventures (sorted by stage number)
    const completedStageAttempts = isMultiStage && attempt.stage_attempts
        ? attempt.stage_attempts
            .filter(sa => sa.status.value === 'success' || sa.status.value === 'partial' || sa.status.value === 'failure')
            .sort((a, b) => a.stage_number - b.stage_number)
        : [];

    // Get stage outcome label and color
    const getStageOutcome = (stageAttempt: typeof completedStageAttempts[0]) => {
        if (stageAttempt.status.value === 'success') return { label: 'Success', color: 'text-green-700' };
        if (stageAttempt.status.value === 'partial') return { label: 'Partial Success', color: 'text-amber-700' };
        return { label: 'Failure', color: 'text-red-700' };
    };

    return (
        <div className="space-y-6 max-w-2xl mx-auto">
            {/* Back link */}
            <button
                onClick={() => router.visit(`/hex/${hexId}?tab=attempts`)}
                className="inline-flex items-center text-amber-400 hover:text-amber-300 transition-colors font-serif"
            >
                ← Back to Quest Dispatches
            </button>

            {/* ============================================ */}
            {/* PARCHMENT SECTION - Story/Narrative Content */}
            {/* ============================================ */}
            <PaperCard variant="aged">
                {/* Adventure Title */}
                <h1 className="text-3xl font-serif text-amber-900 tracking-wide mb-4 text-center">
                    {attempt.adventure?.name || 'Unknown Adventure'}
                </h1>

                {/* Multi-stage badge */}
                {isMultiStage && (
                    <div className="text-center mb-4">
                        <span className="px-3 py-1 bg-purple-700 text-white text-sm font-serif rounded">
                            Multi-Stage Quest ({attempt.current_stage} of {attempt.total_stages})
                        </span>
                    </div>
                )}

                {/* Decorative divider */}
                <div className="flex items-center justify-center gap-4 mb-6">
                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-800/30 to-transparent" />
                    <span className="text-amber-800/40">❧</span>
                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-800/30 to-transparent" />
                </div>

                {/* Description */}
                {attempt.adventure?.description && (
                    <div className="text-amber-900/80 mb-6 font-serif leading-relaxed prose prose-amber max-w-none prose-headings:text-amber-900 prose-p:text-amber-900/80">
                        <EditorRenderer content={attempt.adventure.description} theme="parchment" />
                    </div>
                )}

                {/* SINGLE-STAGE: The Story Begins - Dialogue (before outcome) */}
                {!isMultiStage && attempt.adventure?.dialogue_start && (
                    <div className="relative mt-6 pt-6 border-t border-amber-800/20">
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#e8d5b7] px-4">
                            <span className="text-amber-800/60 font-serif italic text-sm">The Story Begins...</span>
                        </div>

                        <div className="relative">
                            <div className="absolute -top-2 -left-2 text-amber-800/20 text-4xl font-serif">"</div>
                            <div className="relative z-10 text-amber-900/80 leading-relaxed font-serif italic pl-6 pr-6">
                                <EditorRenderer content={attempt.adventure.dialogue_start} theme="parchment" />
                            </div>
                            <div className="absolute -bottom-2 -right-2 text-amber-800/20 text-4xl font-serif rotate-180">"</div>
                        </div>
                    </div>
                )}

                {/* SINGLE-STAGE: Outcome Dialogue - for completed attempts */}
                {!isMultiStage && isCompleted && outcomeDialogue && (
                    <div className="relative mt-6 pt-6 border-t border-amber-800/20">
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#e8d5b7] px-4">
                            <span className={`font-serif italic text-sm ${
                                isSuccess ? 'text-green-700' : isPartial ? 'text-amber-700' : 'text-red-700'
                            }`}>
                                {isSuccess ? 'Victory!' : isPartial ? 'A Close Call...' : 'Defeat...'}
                            </span>
                        </div>

                        <div className="relative">
                            <div className="absolute -top-2 -left-2 text-amber-800/20 text-4xl font-serif">"</div>
                            <div className="relative z-10 text-amber-900/80 leading-relaxed font-serif italic pl-6 pr-6">
                                <EditorRenderer content={outcomeDialogue} theme="parchment" />
                            </div>
                            <div className="absolute -bottom-2 -right-2 text-amber-800/20 text-4xl font-serif rotate-180">"</div>
                        </div>
                    </div>
                )}

                {/* MULTI-STAGE: Stage Dialogues for completed stages */}
                {isMultiStage && completedStageAttempts.length > 0 && (
                    <div className="space-y-6 mt-6">
                        {completedStageAttempts.map((stageAttempt) => {
                            const stage = stageAttempt.stage;
                            const outcome = getStageOutcome(stageAttempt);
                            const isStageSuccess = stageAttempt.status.value === 'success';
                            const isStagePartial = stageAttempt.status.value === 'partial';
                            const isStagePassedOverall = isStageSuccess || isStagePartial;
                            const stageOutcomeDialogue = isStagePassedOverall ? stage?.dialogue_success : stage?.dialogue_failure;

                            return (
                                <div key={stageAttempt.id} className="border-t border-amber-800/20 pt-6">
                                    {/* Stage Header */}
                                    <div className="flex items-center justify-center gap-3 mb-4">
                                        <span className="px-2 py-0.5 bg-purple-700 text-white text-xs font-serif rounded">
                                            Stage {stageAttempt.stage_number}
                                        </span>
                                        <span className={`font-serif text-sm font-medium ${outcome.color}`}>
                                            {outcome.label}
                                        </span>
                                        {stageAttempt.is_critical && (
                                            <span className="text-amber-600 text-xs">⭐ Critical!</span>
                                        )}
                                    </div>

                                    {/* Pre-Stage Dialogue */}
                                    {stage?.dialogue_pre && (
                                        <div className="relative mb-4">
                                            <div className="text-center mb-2">
                                                <span className="text-amber-800/60 font-serif italic text-xs">The Challenge...</span>
                                            </div>
                                            <div className="relative">
                                                <div className="absolute -top-1 -left-1 text-amber-800/20 text-2xl font-serif">"</div>
                                                <div className="relative z-10 text-amber-900/70 leading-relaxed font-serif italic pl-4 pr-4 text-sm">
                                                    <EditorRenderer content={stage.dialogue_pre} theme="parchment" />
                                                </div>
                                                <div className="absolute -bottom-1 -right-1 text-amber-800/20 text-2xl font-serif rotate-180">"</div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Outcome Dialogue */}
                                    {stageOutcomeDialogue && (
                                        <div className="relative">
                                            <div className="text-center mb-2">
                                                <span className={`font-serif italic text-xs ${outcome.color}`}>
                                                    {isStageSuccess ? 'Success!' : isStagePartial ? 'A Close Call...' : 'Failure...'}
                                                </span>
                                            </div>
                                            <div className="relative">
                                                <div className="absolute -top-1 -left-1 text-amber-800/20 text-2xl font-serif">"</div>
                                                <div className="relative z-10 text-amber-900/80 leading-relaxed font-serif italic pl-4 pr-4 text-sm">
                                                    <EditorRenderer content={stageOutcomeDialogue} theme="parchment" />
                                                </div>
                                                <div className="absolute -bottom-1 -right-1 text-amber-800/20 text-2xl font-serif rotate-180">"</div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Stage Roll Result with Per-Challenge Details */}
                                    {stageAttempt.roll_result !== null && (
                                        <div className="mt-3 p-3 bg-amber-100/50 rounded border border-amber-800/10">
                                            {stageAttempt.challenge_results && stageAttempt.challenge_results.length > 0 ? (
                                                <div className="text-amber-900/80 text-xs font-serif space-y-2">
                                                    {stageAttempt.challenge_results.map((cr, idx) => {
                                                        const crSuccess = cr.outcome === 'success' || cr.outcome === 'critical_success';
                                                        const crPartial = cr.outcome === 'partial_success';
                                                        const crCritical = cr.outcome === 'critical_success';
                                                        const outcomeColor = crSuccess
                                                            ? 'text-green-700'
                                                            : crPartial
                                                            ? 'text-amber-700'
                                                            : 'text-red-700';

                                                        return (
                                                            <div key={idx} className="border-b border-amber-800/10 pb-1.5 last:border-b-0 last:pb-0">
                                                                <p>
                                                                    <span className="font-semibold capitalize">{cr.stat_type}</span>
                                                                    {' '}(difficulty {cr.difficulty}):{' '}
                                                                    {cr.character_name ? (
                                                                        <>
                                                                            <span className="font-semibold">{cr.character_name}</span>
                                                                            {' '}had the best score at {cr.character_score},
                                                                            {' '}rolled <span className="font-bold">{cr.roll}</span>
                                                                            {crCritical && ' (doubles!)'}
                                                                            {' '}and needed {'<='}{cr.success_chance}%.
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            Roll: {cr.roll} (needed {'<='}{cr.success_chance}%)
                                                                        </>
                                                                    )}
                                                                </p>
                                                                <p className={outcomeColor}>
                                                                    {crCritical ? 'Critical Success!' :
                                                                     crSuccess ? 'Success!' :
                                                                     crPartial ? 'Partial Success' :
                                                                     'Failed!'}
                                                                </p>
                                                            </div>
                                                        );
                                                    })}
                                                    <div className="pt-1 border-t border-amber-800/20 font-medium">
                                                        Overall: <span className={
                                                            isStageSuccess ? 'text-green-700' :
                                                            isStagePartial ? 'text-amber-700' :
                                                            'text-red-700'
                                                        }>
                                                            {isStageSuccess ? 'Stage Passed' : isStagePartial ? 'Stage Partial' : 'Stage Failed'}
                                                        </span>
                                                    </div>
                                                </div>
                                            ) : stageAttempt.rolling_character ? (
                                                <div className="text-amber-900/80 text-xs font-serif space-y-1">
                                                    <p>
                                                        <span className="font-semibold">{stageAttempt.rolling_character.name}</span>
                                                        {stageAttempt.rolling_skill && stageAttempt.rolling_skill_value !== null && (
                                                            <> had the highest <span className="font-semibold capitalize">{stageAttempt.rolling_skill}</span> at {stageAttempt.rolling_skill_value}</>
                                                        )}
                                                        {stageAttempt.rolling_character.pronouns ? (
                                                            <>, so <span className="font-semibold">{stageAttempt.rolling_character.pronouns.split('/')[0]}</span> rolled</>
                                                        ) : (
                                                            <> and rolled</>
                                                        )}
                                                        {' '}and got <span className="font-bold">{stageAttempt.roll_result}</span>.
                                                    </p>
                                                    <p className={isStageSuccess ? 'text-green-700' : isStagePartial ? 'text-amber-700' : 'text-red-700'}>
                                                        {(() => {
                                                            const pronoun = stageAttempt.rolling_character.pronouns ? stageAttempt.rolling_character.pronouns.split('/')[0] : 'they';
                                                            if (isStageSuccess) {
                                                                return `This was a success since ${pronoun} needed ≤${stageAttempt.success_chance}%.`;
                                                            } else if (isStagePartial) {
                                                                return `This was a partial success — ${pronoun} needed ≤${stageAttempt.success_chance}%, but ${pronoun} came close enough to scrape by.`;
                                                            } else {
                                                                return `This was a failure since ${pronoun} needed ≤${stageAttempt.success_chance}%.`;
                                                            }
                                                        })()}
                                                    </p>
                                                </div>
                                            ) : (
                                                <div className="text-center text-amber-800/60 text-xs font-serif">
                                                    Roll: {stageAttempt.roll_result} (needed ≤{stageAttempt.success_chance}%)
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* MULTI-STAGE: Current Stage In Progress */}
                {isMultiStage && isInProgress && (() => {
                    const currentStageAttempt = attempt.current_stage_attempt;
                    const currentStageDialogue = currentStageAttempt?.stage?.dialogue_pre;

                    return (
                        <div className="relative mt-6 pt-6 border-t border-amber-800/20">
                            {/* Stage Header */}
                            <div className="flex items-center justify-center gap-3 mb-4">
                                <span className="px-2 py-0.5 bg-purple-700 text-white text-xs font-serif rounded">
                                    Stage {attempt.current_stage}
                                </span>
                                <span className="font-serif text-sm font-medium text-purple-700">
                                    In Progress
                                </span>
                            </div>

                            {/* Current Stage Dialogue */}
                            {currentStageDialogue ? (
                                <div className="relative mb-4">
                                    <div className="text-center mb-2">
                                        <span className="text-amber-800/60 font-serif italic text-xs">The Challenge Ahead...</span>
                                    </div>
                                    <div className="relative">
                                        <div className="absolute -top-1 -left-1 text-amber-800/20 text-2xl font-serif">"</div>
                                        <div className="relative z-10 text-amber-900/80 leading-relaxed font-serif italic pl-4 pr-4">
                                            <EditorRenderer content={currentStageDialogue} theme="parchment" />
                                        </div>
                                        <div className="absolute -bottom-1 -right-1 text-amber-800/20 text-2xl font-serif rotate-180">"</div>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-amber-800/60 font-serif italic text-sm text-center">
                                    Your adventurers face the next challenge...
                                </p>
                            )}
                        </div>
                    );
                })()}
            </PaperCard>

            {/* ============================================ */}
            {/* OFFICIAL FORM - Quest Report Document */}
            {/* ============================================ */}
            <AttemptReportForm attempt={attempt} isInProgress={isInProgress} isCompleted={isCompleted} isGm={isGm} />
        </div>
    );
}

/**
 * AttemptReportForm - Official quest report document (mechanical details).
 */
function AttemptReportForm({
    attempt,
    isInProgress,
    isCompleted,
    isGm,
}: {
    attempt: AdventureAttempt;
    isInProgress: boolean;
    isCompleted: boolean;
    isGm: boolean;
}) {
    const isMultiStage = attempt.is_multi_stage;
    const currentStageAttempt = attempt.current_stage_attempt;

    // Stage duration in seconds (10 minutes per stage)
    const STAGE_DURATION_SECONDS = 10 * 60;

    // For multi-stage: use current stage's remaining time; for single-stage: use attempt's remaining time
    const initialCurrentStageSeconds = isMultiStage && currentStageAttempt
        ? currentStageAttempt.remaining_seconds
        : attempt.remaining_seconds;

    // Calculate total remaining time including future stages
    const calculateTotalRemaining = (currentStageSeconds: number) => {
        if (!isMultiStage) return currentStageSeconds;
        const remainingStagesAfterCurrent = attempt.total_stages - attempt.current_stage;
        return currentStageSeconds + (remainingStagesAfterCurrent * STAGE_DURATION_SECONDS);
    };

    const [currentStageSeconds, setCurrentStageSeconds] = useState(initialCurrentStageSeconds);
    // Total remaining includes current stage + future stages
    const remainingSeconds = calculateTotalRemaining(currentStageSeconds);
    const [isCompleting, setIsCompleting] = useState(false);
    const [isClaiming, setIsClaiming] = useState(false);

    // For multi-stage: check if current stage is ready; for single-stage: check overall readiness
    // Use currentStageSeconds (not total remainingSeconds) for stage readiness
    const isStageReady = isMultiStage && currentStageAttempt
        ? currentStageSeconds <= 0
        : attempt.is_ready_to_complete || currentStageSeconds <= 0;

    // Is adventure ready for healing (between stages)?
    const isReadyForHealing = attempt.is_ready_for_healing;

    // Countdown timer for in-progress - decrements current stage seconds
    useEffect(() => {
        if (!isInProgress || currentStageSeconds <= 0) return;

        const timer = setInterval(() => {
            setCurrentStageSeconds((prev) => Math.max(0, prev - 1));
        }, 1000);

        return () => clearInterval(timer);
    }, [isInProgress, currentStageSeconds]);

    // Handle complete button - for single-stage or completing a stage in multi-stage
    const handleComplete = () => {
        setIsCompleting(true);
        if (isMultiStage) {
            // Complete current stage
            router.post(`/adventures/attempts/${attempt.id}/complete-stage`, {}, {
                onFinish: () => setIsCompleting(false),
            });
        } else {
            // Complete single-stage adventure
            router.post(`/adventures/attempts/${attempt.id}/complete`, {}, {
                onFinish: () => setIsCompleting(false),
            });
        }
    };

    // Handle claim rewards
    const handleClaim = () => {
        setIsClaiming(true);
        if (isMultiStage) {
            router.post(`/adventures/attempts/${attempt.id}/claim-multistage`, {}, {
                onFinish: () => setIsClaiming(false),
            });
        } else {
            router.post(`/adventures/attempts/${attempt.id}/claim`, {}, {
                onFinish: () => setIsClaiming(false),
            });
        }
    };

    // Handle go to healing phase
    const handleGoToHealing = () => {
        router.visit(`/adventures/attempts/${attempt.id}/healing`);
    };

    // Format time remaining
    const formatTime = (seconds: number): string => {
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        if (hrs > 0) return `${hrs}h ${mins}m ${secs}s`;
        if (mins > 0) return `${mins}m ${secs}s`;
        return `${secs}s`;
    };

    // Get status stamp info
    const getStatusStamp = () => {
        if (isMultiStage && isInProgress) {
            if (isReadyForHealing) {
                return { text: 'STAGE', subText: 'COMPLETE', color: 'rgba(147, 51, 234, 0.8)', rotation: -8 };
            }
            if (isStageReady) {
                return { text: 'STAGE', subText: 'READY', color: 'rgba(22, 163, 74, 0.8)', rotation: -8 };
            }
            return { text: `STAGE ${attempt.current_stage}`, subText: `OF ${attempt.total_stages}`, color: 'rgba(147, 51, 234, 0.8)', rotation: -5 };
        }
        if (isInProgress && isStageReady) {
            return { text: 'READY', subText: 'TO COMPLETE', color: 'rgba(22, 163, 74, 0.8)', rotation: -8 };
        }
        if (isInProgress) {
            return { text: 'IN PROGRESS', color: 'rgba(59, 130, 246, 0.8)', rotation: -5 };
        }
        if (attempt.status.value === 'success') {
            return { text: 'SUCCESS', color: 'rgba(22, 163, 74, 0.8)', rotation: -12 };
        }
        if (attempt.status.value === 'partial') {
            return { text: 'PARTIAL', subText: 'SUCCESS', color: 'rgba(202, 138, 4, 0.8)', rotation: -6 };
        }
        if (attempt.status.value === 'failure') {
            return { text: 'FAILED', color: 'rgba(220, 38, 38, 0.8)', rotation: -10 };
        }
        return { text: attempt.status.label.toUpperCase(), color: 'rgba(107, 114, 128, 0.8)', rotation: -7 };
    };

    const statusStamp = getStatusStamp();

    // Get completed stage attempts for progress display
    const completedStageAttempts = isMultiStage && attempt.stage_attempts
        ? attempt.stage_attempts
            .filter(sa => sa.status.value === 'success' || sa.status.value === 'partial' || sa.status.value === 'failure')
            .sort((a, b) => a.stage_number - b.stage_number)
        : [];

    return (
        <div
            className="relative rounded-lg overflow-hidden"
            style={{
                background: 'linear-gradient(135deg, #faf8f0 0%, #f5f0e1 50%, #faf8f0 100%)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15), inset 0 0 60px rgba(139, 90, 43, 0.05)',
                border: '2px solid #c9a961',
            }}
        >
            {/* Decorative corner flourishes */}
            <div className="absolute top-2 left-2 text-amber-700/20 text-2xl">❦</div>
            <div className="absolute top-2 right-2 text-amber-700/20 text-2xl rotate-90">❦</div>
            <div className="absolute bottom-2 left-2 text-amber-700/20 text-2xl -rotate-90">❦</div>
            <div className="absolute bottom-2 right-2 text-amber-700/20 text-2xl rotate-180">❦</div>

            {/* Form Header */}
            <div
                className="px-6 py-4 text-center border-b-2 relative"
                style={{
                    background: 'linear-gradient(180deg, rgba(139, 90, 43, 0.15) 0%, rgba(139, 90, 43, 0.05) 100%)',
                    borderColor: '#c9a961',
                }}
            >
                <div className="text-xs font-serif text-amber-800/60 tracking-widest uppercase mb-1">
                    Adventurer's Guild Official Document
                </div>
                <h2 className="text-xl font-serif font-bold text-amber-900 tracking-wide">
                    Quest Completion Report
                </h2>
                <div className="text-sm font-serif text-amber-800/70 mt-1">
                    Report #QCR-{attempt.id.toString().padStart(4, '0')}
                </div>

                {/* Status Stamp in header */}
                <div
                    className="absolute top-3 right-4"
                    style={{ transform: `rotate(${statusStamp.rotation}deg)` }}
                >
                    <div
                        className="px-3 py-1.5 rounded-sm border-3 border-dashed"
                        style={{
                            borderColor: statusStamp.color,
                            background: `linear-gradient(135deg, ${statusStamp.color.replace('0.8', '0.1')} 0%, ${statusStamp.color.replace('0.8', '0.15')} 100%)`,
                        }}
                    >
                        <div className="absolute inset-0.5 rounded-sm border" style={{ borderColor: statusStamp.color }} />
                        <div className="relative text-center">
                            <div
                                className="text-xs font-serif font-bold uppercase tracking-wider"
                                style={{ color: statusStamp.color.replace('0.8', '1') }}
                            >
                                {statusStamp.text}
                            </div>
                            {statusStamp.subText && (
                                <div
                                    className="text-[10px] font-serif uppercase tracking-wider -mt-0.5"
                                    style={{ color: statusStamp.color.replace('0.8', '0.9') }}
                                >
                                    {statusStamp.subText}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Form Body */}
            <div className="p-6 space-y-6">
                {/* Nixie Tube Timer - shown when in progress */}
                {isInProgress && (
                    <NixieTubeTimerBox
                        currentStageSeconds={currentStageSeconds}
                        totalSeconds={remainingSeconds}
                        isMultiStage={isMultiStage}
                        currentStage={attempt.current_stage}
                        totalStages={attempt.total_stages}
                        isComplete={isStageReady}
                    />
                )}

                {/* Assigned Personnel Section */}
                <div className="border-2 border-amber-800/20 rounded overflow-hidden bg-white/50">
                    <div className="bg-amber-100/50 px-4 py-2 border-b border-amber-800/20">
                        <h3 className="font-serif font-semibold text-amber-900 text-sm uppercase tracking-wide">
                            § Assigned Personnel
                        </h3>
                    </div>
                    <div className="p-4">
                        <div className="flex flex-wrap gap-2">
                            {attempt.characters.map((char) => (
                                <div
                                    key={char.id}
                                    className="px-3 py-1.5 rounded border border-amber-800/30 font-serif text-amber-900 text-sm"
                                    style={{
                                        background: 'linear-gradient(135deg, rgba(245,230,211,0.8), rgba(232,213,183,0.8))',
                                    }}
                                >
                                    {char.name}
                                    {char.xp_awarded > 0 && (
                                        <span className="ml-2 text-green-700 font-medium">+{char.xp_awarded} XP</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Mission Timeline */}
                <div className="border-2 border-amber-800/20 rounded overflow-hidden bg-white/50">
                    <div className="bg-amber-100/50 px-4 py-2 border-b border-amber-800/20">
                        <h3 className="font-serif font-semibold text-amber-900 text-sm uppercase tracking-wide">
                            § Mission Timeline
                        </h3>
                    </div>
                    <div className="p-4 grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-serif text-amber-800/70 uppercase tracking-wide mb-1">
                                Departed
                            </label>
                            <div className="text-sm font-serif font-medium text-amber-900">
                                {attempt.started_at ? formatAttemptDate(attempt.started_at) : 'N/A'}
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-serif text-amber-800/70 uppercase tracking-wide mb-1">
                                {isCompleted ? 'Returned' : 'Status'}
                            </label>
                            <div className="text-sm font-serif font-medium text-amber-900">
                                {isCompleted && attempt.completed_at
                                    ? formatAttemptDate(attempt.completed_at)
                                    : isStageReady
                                    ? 'Awaiting Review'
                                    : isInProgress
                                    ? 'In Transit'
                                    : 'N/A'}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Multi-Stage Progress Section */}
                {isMultiStage && isInProgress && (
                    <div className="border-2 border-purple-800/30 rounded overflow-hidden bg-white/50">
                        <div className="bg-purple-100/50 px-4 py-2 border-b border-purple-800/20">
                            <h3 className="font-serif font-semibold text-purple-900 text-sm uppercase tracking-wide">
                                § Stage Progress
                            </h3>
                        </div>
                        <div className="p-4">
                            {/* Stage Progress Indicator */}
                            <div className="flex items-center justify-center gap-2 mb-4">
                                {Array.from({ length: attempt.total_stages }, (_, i) => i + 1).map((stageNum) => {
                                    const stageAttempt = attempt.stage_attempts?.find(sa => sa.stage_number === stageNum);
                                    const isComplete = stageAttempt && (stageAttempt.status.value === 'success' || stageAttempt.status.value === 'partial' || stageAttempt.status.value === 'failure');
                                    const isCurrent = stageNum === attempt.current_stage;
                                    const isSuccess = stageAttempt?.status.value === 'success' || stageAttempt?.status.value === 'partial';

                                    return (
                                        <div key={stageNum} className="flex items-center">
                                            <div
                                                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-serif font-bold border-2 ${
                                                    isComplete
                                                        ? isSuccess
                                                            ? 'bg-green-600 border-green-600 text-white'
                                                            : 'bg-red-600 border-red-600 text-white'
                                                        : isCurrent
                                                            ? 'bg-purple-600 border-purple-600 text-white ring-2 ring-purple-400 ring-offset-1'
                                                            : 'bg-gray-200 border-gray-300 text-gray-500'
                                                }`}
                                            >
                                                {isComplete ? (isSuccess ? '✓' : '✕') : stageNum}
                                            </div>
                                            {stageNum < attempt.total_stages && (
                                                <div className={`w-6 h-1 ${isComplete ? (isSuccess ? 'bg-green-600' : 'bg-red-600') : 'bg-gray-300'}`} />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Completed Stages Results */}
                            {completedStageAttempts.length > 0 && (
                                <div className="space-y-4 mb-4">
                                    {completedStageAttempts.map((stageAttempt) => {
                                        const isSuccess = stageAttempt.status.value === 'success';
                                        const isPartial = stageAttempt.status.value === 'partial';
                                        const isPassed = isSuccess || isPartial;
                                        const outcomeDialogue = isPassed
                                            ? stageAttempt.stage?.dialogue_success
                                            : stageAttempt.stage?.dialogue_failure;

                                        const bgClass = isSuccess ? 'bg-green-50/50 border-green-800/20' : isPartial ? 'bg-amber-50/50 border-amber-800/20' : 'bg-red-50/50 border-red-800/20';
                                        const textClass = isSuccess ? 'text-green-700' : isPartial ? 'text-amber-700' : 'text-red-700';
                                        const outcomeBgClass = isSuccess ? 'bg-green-100/50 border-green-800/10' : isPartial ? 'bg-amber-100/50 border-amber-800/10' : 'bg-red-100/50 border-red-800/10';
                                        const outcomeLabelClass = isSuccess ? 'text-green-800/50' : isPartial ? 'text-amber-800/50' : 'text-red-800/50';
                                        const outcomeTextClass = isSuccess ? 'text-green-900/80' : isPartial ? 'text-amber-900/80' : 'text-red-900/80';
                                        const outcomeLabel = isSuccess ? 'Success' : isPartial ? 'Partial Success' : 'Failure';
                                        const rollOutcomeLabel = isSuccess ? 'Success!' : isPartial ? 'Partial!' : 'Failed';

                                        return (
                                            <div key={stageAttempt.id} className={`p-3 rounded border ${bgClass}`}>
                                                {/* Stage Header */}
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="font-serif font-semibold text-amber-900">
                                                        Stage {stageAttempt.stage_number}
                                                    </span>
                                                    <span className={`font-serif font-medium ${textClass}`}>
                                                        {stageAttempt.status.label}
                                                        {stageAttempt.is_critical && ' ⭐'}
                                                    </span>
                                                </div>

                                                {/* Roll Details */}
                                                {stageAttempt.roll_result !== null && (
                                                    <div className="mb-2 p-2 bg-amber-50/50 border border-amber-800/10 rounded text-xs font-serif">
                                                        {stageAttempt.rolling_character ? (
                                                            <p className="text-amber-900/80">
                                                                <span className="font-semibold">{stageAttempt.rolling_character.name}</span>
                                                                {stageAttempt.rolling_skill && stageAttempt.rolling_skill_value !== null && (
                                                                    <> (<span className="capitalize">{stageAttempt.rolling_skill}</span>: {stageAttempt.rolling_skill_value})</>
                                                                )}
                                                                {' '}rolled <span className="font-bold">{stageAttempt.roll_result}</span> vs ≤{stageAttempt.success_chance}%
                                                                {' '}<span className={`${textClass} font-semibold`}>
                                                                    ({rollOutcomeLabel})
                                                                </span>
                                                            </p>
                                                        ) : (
                                                            <span className="text-amber-800/60">
                                                                Roll: {stageAttempt.roll_result} / {stageAttempt.success_chance}%
                                                            </span>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Pre-Stage Dialogue */}
                                                {stageAttempt.stage?.dialogue_pre && (
                                                    <div className="mb-2 p-2 bg-amber-50/50 border border-amber-800/10 rounded">
                                                        <div className="text-xs font-serif text-amber-800/50 uppercase tracking-wide mb-1">
                                                            Briefing
                                                        </div>
                                                        <div className="text-amber-900/70 font-serif text-sm">
                                                            <EditorRenderer content={stageAttempt.stage.dialogue_pre} theme="parchment" />
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Outcome Dialogue */}
                                                {outcomeDialogue && (
                                                    <div className={`p-2 rounded border ${outcomeBgClass}`}>
                                                        <div className={`text-xs font-serif uppercase tracking-wide mb-1 ${outcomeLabelClass}`}>
                                                            {outcomeLabel}
                                                        </div>
                                                        <div className={`font-serif text-sm ${outcomeTextClass}`}>
                                                            <EditorRenderer content={outcomeDialogue} theme="parchment" />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Current Stage Status */}
                            {currentStageAttempt && !isReadyForHealing && (
                                <div className="p-4 bg-purple-50/50 border border-purple-800/20 rounded">
                                    <div className="text-center text-purple-900 font-serif font-medium mb-3">
                                        Stage {attempt.current_stage} of {attempt.total_stages}
                                    </div>

                                    {/* Current Stage Dialogue */}
                                    {currentStageAttempt.stage?.dialogue_pre && (
                                        <div className="mb-4 p-3 bg-amber-50/50 border border-amber-800/20 rounded">
                                            <div className="text-xs font-serif text-amber-800/60 uppercase tracking-wide mb-2">
                                                Stage {attempt.current_stage} Briefing
                                            </div>
                                            <div className="text-amber-900/80 font-serif text-sm leading-relaxed">
                                                <EditorRenderer content={currentStageAttempt.stage.dialogue_pre} theme="parchment" />
                                            </div>
                                        </div>
                                    )}

                                    <div className="text-center">
                                        {isStageReady ? (
                                            <>
                                                <div className="text-purple-700 text-lg mb-2 font-serif animate-pulse">
                                                    🎲 Rolling dice...
                                                </div>
                                                <p className="text-amber-800/70 font-serif italic text-sm">
                                                    Results will appear shortly.
                                                </p>
                                            </>
                                        ) : (
                                            <p className="text-amber-800/70 font-serif italic text-sm">
                                                Your party is making progress through this stage.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Ready for Healing Phase */}
                            {isReadyForHealing && (
                                <div className="text-center p-4 bg-green-50/50 border border-green-800/20 rounded">
                                    <div className="text-green-700 text-lg mb-2 font-serif">
                                        ✓ Stage {attempt.current_stage - 1} Complete!
                                    </div>
                                    <p className="text-amber-800/70 font-serif italic text-sm mb-3">
                                        Your party may rest and heal before continuing to Stage {attempt.current_stage}.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Single-Stage In Progress: Status */}
                {!isMultiStage && isInProgress && (
                    <div className="border-2 border-amber-800/20 rounded overflow-hidden bg-white/50">
                        <div className="bg-amber-100/50 px-4 py-2 border-b border-amber-800/20">
                            <h3 className="font-serif font-semibold text-amber-900 text-sm uppercase tracking-wide">
                                § Mission Status
                            </h3>
                        </div>
                        <div className="p-4 text-center">
                            {isStageReady ? (
                                <>
                                    <div className="text-green-700 text-lg mb-3 font-serif">
                                        ✓ Your adventurers have returned!
                                    </div>
                                    <p className="text-amber-800/70 mb-4 font-serif italic text-sm">
                                        Complete the mission to see results and claim rewards.
                                    </p>
                                </>
                            ) : (
                                <>
                                    <p className="text-amber-800/70 font-serif italic text-sm mb-4">
                                        Your party is on their expedition.
                                    </p>
                                    {attempt.success_chance != null && (
                                        <div className="text-sm font-serif text-amber-800">
                                            Estimated success chance: <span className="font-bold">{Math.round(attempt.success_chance)}%</span>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )}

                {/* Completed: Roll Results */}
                {isCompleted && attempt.roll_result != null && (
                    <div className="border-2 border-amber-800/20 rounded overflow-hidden bg-white/50">
                        <div className="bg-amber-100/50 px-4 py-2 border-b border-amber-800/20">
                            <h3 className="font-serif font-semibold text-amber-900 text-sm uppercase tracking-wide">
                                § Outcome Assessment
                            </h3>
                        </div>
                        <div className="p-4">
                            <div className="flex items-center justify-center gap-8 mb-4">
                                <div className="text-center">
                                    <div className="text-4xl font-serif font-bold text-amber-900">
                                        {attempt.roll_result}
                                    </div>
                                    <div className="text-sm text-amber-800/60 font-serif italic">Your Roll</div>
                                </div>
                                <div className="text-2xl text-amber-800/40 font-serif">vs</div>
                                <div className="text-center">
                                    <div className="text-4xl font-serif font-bold text-amber-800/70">
                                        {attempt.success_chance}%
                                    </div>
                                    <div className="text-sm text-amber-800/60 font-serif italic">Target</div>
                                </div>
                            </div>
                            {attempt.is_critical && (
                                <div className="text-center text-amber-700 font-serif font-medium">
                                    ⭐ Critical Roll! (Doubles) - Double Rewards! ⭐
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Rewards Section */}
                {isCompleted && (() => {
                    // For multi-stage adventures, collect all rewards from stages + accumulated
                    let allRewards: GrantedReward[] = [];

                    if (isMultiStage && !attempt.rewards_claimed) {
                        // Before claiming: gather from accumulated_rewards and all stage attempts
                        if (attempt.accumulated_rewards) {
                            allRewards = [...allRewards, ...attempt.accumulated_rewards];
                        }
                        if (attempt.stage_attempts) {
                            attempt.stage_attempts.forEach((stage) => {
                                if (stage.rewards_granted) {
                                    allRewards = [...allRewards, ...stage.rewards_granted];
                                }
                            });
                        }
                    } else {
                        // Single-stage or after claiming: use rewards_granted
                        allRewards = attempt.rewards_granted || [];
                    }

                    if (allRewards.length > 0) {
                        return (
                            <div className="border-2 border-amber-800/20 rounded overflow-hidden bg-white/50">
                                <div className="bg-amber-100/50 px-4 py-2 border-b border-amber-800/20">
                                    <h3 className="font-serif font-semibold text-amber-900 text-sm uppercase tracking-wide">
                                        § Compensation {attempt.rewards_claimed ? 'Received' : 'Earned'}
                                    </h3>
                                </div>
                                <div className="p-4 space-y-2">
                                    {allRewards.map((reward, index) => (
                                        <AttemptRewardItem key={index} reward={reward} />
                                    ))}
                                </div>
                            </div>
                        );
                    } else {
                        return (
                            <div className="border-2 border-amber-800/20 rounded overflow-hidden bg-white/50">
                                <div className="bg-amber-100/50 px-4 py-2 border-b border-amber-800/20">
                                    <h3 className="font-serif font-semibold text-amber-900 text-sm uppercase tracking-wide">
                                        § Compensation
                                    </h3>
                                </div>
                                <div className="p-4 text-center">
                                    <p className="text-amber-800/60 font-serif italic">
                                        No rewards were granted for this mission.
                                    </p>
                                </div>
                            </div>
                        );
                    }
                })()}

                {/* Authorization / Action Section */}
                <div className="border-t-2 border-amber-800/30 pt-6">
                    {/* Multi-Stage: In Progress - No button needed, stages auto-complete */}
                    {isMultiStage && isInProgress && (
                        <div className="text-center">
                            <p className="text-amber-800/60 font-serif italic text-sm">
                                Stages complete automatically. Refresh to see updates.
                            </p>
                        </div>
                    )}

                    {/* Single-Stage: Ready - Complete Mission Button */}
                    {!isMultiStage && isInProgress && isStageReady && (
                        <div className="flex justify-center">
                            <div
                                className="relative group cursor-pointer"
                                onClick={handleComplete}
                            >
                                <div
                                    className={`relative p-4 rounded-lg border-4 border-dashed transition-all duration-200 ${
                                        !isCompleting ? 'group-hover:translate-y-0.5 group-hover:translate-x-0.5' : ''
                                    }`}
                                    style={{
                                        borderColor: isCompleting ? 'rgba(120, 100, 80, 0.3)' : 'rgba(22, 101, 52, 0.6)',
                                        background: isCompleting
                                            ? 'linear-gradient(135deg, rgba(120, 100, 80, 0.08) 0%, rgba(120, 100, 80, 0.15) 100%)'
                                            : 'linear-gradient(135deg, rgba(22, 101, 52, 0.08) 0%, rgba(22, 101, 52, 0.15) 100%)',
                                    }}
                                >
                                    <div className={`absolute inset-2 rounded-lg border-2 ${
                                        isCompleting ? 'border-amber-800/20' : 'border-green-800/30'
                                    }`} />
                                    <div className="relative text-center px-6">
                                        <div className={`text-lg font-serif font-bold tracking-wider uppercase mb-1 ${
                                            isCompleting ? 'text-amber-800/50' : 'text-green-900'
                                        }`}>
                                            {isCompleting ? 'Rolling...' : '🎲 Complete'}
                                        </div>
                                        <div className={`text-xs font-serif ${
                                            isCompleting ? 'text-amber-800/40' : 'text-green-800'
                                        }`}>
                                            MISSION
                                        </div>
                                    </div>
                                </div>
                                {!isCompleting && (
                                    <div className="absolute inset-0 rounded-lg bg-green-900/10 opacity-0 group-hover:opacity-100 transition-opacity -z-10 translate-y-1 translate-x-1" />
                                )}
                            </div>
                        </div>
                    )}

                    {/* Single-stage claim button */}
                    {!isMultiStage && isCompleted && !attempt.rewards_claimed && attempt.rewards_granted && attempt.rewards_granted.length > 0 && (
                        <div className="flex justify-center">
                            <div
                                className="relative group cursor-pointer"
                                onClick={handleClaim}
                            >
                                <div
                                    className={`relative p-4 rounded-lg border-4 border-dashed transition-all duration-200 ${
                                        !isClaiming ? 'group-hover:translate-y-0.5 group-hover:translate-x-0.5' : ''
                                    }`}
                                    style={{
                                        borderColor: isClaiming ? 'rgba(120, 100, 80, 0.3)' : 'rgba(161, 98, 7, 0.6)',
                                        background: isClaiming
                                            ? 'linear-gradient(135deg, rgba(120, 100, 80, 0.08) 0%, rgba(120, 100, 80, 0.15) 100%)'
                                            : 'linear-gradient(135deg, rgba(161, 98, 7, 0.08) 0%, rgba(161, 98, 7, 0.15) 100%)',
                                    }}
                                >
                                    <div className={`absolute inset-2 rounded-lg border-2 ${
                                        isClaiming ? 'border-amber-800/20' : 'border-amber-700/30'
                                    }`} />
                                    <div className="relative text-center px-6">
                                        <div className={`text-lg font-serif font-bold tracking-wider uppercase mb-1 ${
                                            isClaiming ? 'text-amber-800/50' : 'text-amber-900'
                                        }`}>
                                            {isClaiming ? 'Claiming...' : '✨ Claim'}
                                        </div>
                                        <div className={`text-xs font-serif ${
                                            isClaiming ? 'text-amber-800/40' : 'text-amber-800'
                                        }`}>
                                            REWARDS
                                        </div>
                                    </div>
                                </div>
                                {!isClaiming && (
                                    <div className="absolute inset-0 rounded-lg bg-amber-900/10 opacity-0 group-hover:opacity-100 transition-opacity -z-10 translate-y-1 translate-x-1" />
                                )}
                            </div>
                        </div>
                    )}

                    {/* Multi-stage claim button - show when completed and not yet claimed */}
                    {isMultiStage && isCompleted && !attempt.rewards_claimed && (
                        <div className="flex justify-center">
                            <div
                                className="relative group cursor-pointer"
                                onClick={handleClaim}
                            >
                                <div
                                    className={`relative p-4 rounded-lg border-4 border-dashed transition-all duration-200 ${
                                        !isClaiming ? 'group-hover:translate-y-0.5 group-hover:translate-x-0.5' : ''
                                    }`}
                                    style={{
                                        borderColor: isClaiming ? 'rgba(120, 100, 80, 0.3)' : 'rgba(161, 98, 7, 0.6)',
                                        background: isClaiming
                                            ? 'linear-gradient(135deg, rgba(120, 100, 80, 0.08) 0%, rgba(120, 100, 80, 0.15) 100%)'
                                            : 'linear-gradient(135deg, rgba(161, 98, 7, 0.08) 0%, rgba(161, 98, 7, 0.15) 100%)',
                                    }}
                                >
                                    <div className={`absolute inset-2 rounded-lg border-2 ${
                                        isClaiming ? 'border-amber-800/20' : 'border-amber-700/30'
                                    }`} />
                                    <div className="relative text-center px-6">
                                        <div className={`text-lg font-serif font-bold tracking-wider uppercase mb-1 ${
                                            isClaiming ? 'text-amber-800/50' : 'text-amber-900'
                                        }`}>
                                            {isClaiming ? 'Claiming...' : '✨ Claim All'}
                                        </div>
                                        <div className={`text-xs font-serif ${
                                            isClaiming ? 'text-amber-800/40' : 'text-amber-800'
                                        }`}>
                                            STAGE REWARDS
                                        </div>
                                    </div>
                                </div>
                                {!isClaiming && (
                                    <div className="absolute inset-0 rounded-lg bg-amber-900/10 opacity-0 group-hover:opacity-100 transition-opacity -z-10 translate-y-1 translate-x-1" />
                                )}
                            </div>
                        </div>
                    )}

                    {isCompleted && attempt.rewards_claimed && (
                        <div className="text-center">
                            <div
                                className="inline-block px-6 py-3 rounded-lg border-2 border-dashed"
                                style={{
                                    borderColor: 'rgba(22, 163, 74, 0.4)',
                                    background: 'linear-gradient(135deg, rgba(22, 163, 74, 0.08) 0%, rgba(22, 163, 74, 0.12) 100%)',
                                }}
                            >
                                <span className="font-serif text-green-800 font-medium">
                                    ✓ Rewards Claimed - Mission Complete
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* GM Tools Section */}
            {isGm && isMultiStage && isInProgress && (
                <GmStageTools attemptId={attempt.id} currentStage={attempt.current_stage} totalStages={attempt.total_stages} />
            )}

            {/* Form Footer */}
            <div
                className="px-6 py-3 text-xs font-serif text-amber-800/50 text-center italic border-t-2"
                style={{ borderColor: '#c9a961', background: 'rgba(139, 90, 43, 0.05)' }}
            >
                This document is property of the Adventurer's Guild. Retain for your records.
            </div>
        </div>
    );
}

/**
 * GmStageTools - GM-only tools for managing multi-stage adventures.
 * Allows force-completing stages for testing purposes.
 */
function GmStageTools({ attemptId, currentStage, totalStages }: { attemptId: number; currentStage: number; totalStages: number }) {
    const [isForceCompleting, setIsForceCompleting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleForceComplete = async () => {
        if (isForceCompleting) return;
        setIsForceCompleting(true);
        setError(null);

        try {
            const csrfToken = document.cookie.split('; ').find(row => row.startsWith('XSRF-TOKEN='))?.split('=')[1];
            const response = await fetch(`/adventures/attempts/${attemptId}/gm-force-complete`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-XSRF-TOKEN': csrfToken ? decodeURIComponent(csrfToken) : '',
                },
                credentials: 'same-origin',
            });

            if (response.ok) {
                // Reload the page to show updated state
                window.location.reload();
            } else {
                const data = await response.json();
                setError(data.error || 'Failed to force-complete stage');
            }
        } catch {
            setError('Failed to force-complete stage');
        } finally {
            setIsForceCompleting(false);
        }
    };

    return (
        <div
            className="mt-4 p-4 rounded-lg border-2 border-dashed"
            style={{
                borderColor: 'rgba(239, 68, 68, 0.4)',
                background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(239, 68, 68, 0.1) 100%)',
            }}
        >
            <div className="flex items-center gap-2 mb-3">
                <span className="text-red-600 text-lg">🔧</span>
                <h4 className="font-serif font-semibold text-red-800 uppercase tracking-wide text-sm">
                    GM Tools
                </h4>
            </div>
            <p className="text-xs text-red-700/70 font-serif mb-3">
                Stage {currentStage} of {totalStages} - Skip timer and force-complete the current stage.
            </p>
            {error && (
                <p className="text-xs text-red-600 font-serif mb-2">{error}</p>
            )}
            <button
                onClick={handleForceComplete}
                disabled={isForceCompleting}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white text-sm font-serif font-medium rounded transition-colors"
            >
                {isForceCompleting ? 'Completing...' : `Force Complete Stage ${currentStage}`}
            </button>
        </div>
    );
}

/**
 * AttemptInProgressSection - Timer and complete button for in-progress attempts.
 * Features paper scrap timer with hand-drawn progress.
 */
function AttemptInProgressSection({ attempt }: { attempt: AdventureAttempt }) {
    const [remainingSeconds, setRemainingSeconds] = useState(attempt.remaining_seconds);
    const [isCompleting, setIsCompleting] = useState(false);
    const isReady = attempt.is_ready_to_complete || remainingSeconds <= 0;

    // Calculate total time in seconds (difficulty * 10 minutes * 60 seconds)
    const totalSeconds = (attempt.adventure?.total_difficulty || 1) * 10 * 60;

    // Countdown timer
    useEffect(() => {
        if (remainingSeconds <= 0) return;

        const timer = setInterval(() => {
            setRemainingSeconds((prev) => Math.max(0, prev - 1));
        }, 1000);

        return () => clearInterval(timer);
    }, [remainingSeconds]);

    // Handle complete button
    const handleComplete = () => {
        setIsCompleting(true);
        router.post(`/adventures/attempts/${attempt.id}/complete`, {}, {
            onFinish: () => setIsCompleting(false),
        });
    };

    return (
        <PaperCard rotation={0.5} variant="torn">
            <h2 className="text-lg font-serif font-semibold text-amber-900 mb-4 flex items-center gap-2">
                <span>⏳</span>
                <span className="italic">Adventure in Progress</span>
            </h2>

            {isReady ? (
                <div className="text-center">
                    <div className="text-green-700 text-xl mb-4 font-serif">
                        ✓ Your characters have returned!
                    </div>
                    <p className="text-amber-800/80 mb-6 font-serif italic">
                        Complete the adventure to see your results and claim rewards.
                    </p>
                    <button
                        onClick={handleComplete}
                        disabled={isCompleting}
                        className={`px-8 py-3 rounded-lg font-serif font-medium text-lg transition-all ${
                            isCompleting
                                ? 'bg-stone-400 text-stone-600 cursor-not-allowed'
                                : 'bg-gradient-to-br from-amber-600 to-amber-800 hover:from-amber-500 hover:to-amber-700 text-amber-100 shadow-lg border border-amber-500/30'
                        }`}
                        style={!isCompleting ? {
                            boxShadow: '0 2px 8px rgba(139,90,43,0.4), inset 0 1px 0 rgba(255,255,255,0.1)',
                        } : undefined}
                    >
                        {isCompleting ? 'Rolling the dice...' : '🎲 Complete Adventure'}
                    </button>
                </div>
            ) : (
                <PaperScrapTimer
                    remainingSeconds={remainingSeconds}
                    totalSeconds={totalSeconds}
                    isReady={isReady}
                />
            )}
        </PaperCard>
    );
}

/**
 * AttemptResultsSection - Shows results for completed attempts with paper aesthetic.
 */
function AttemptResultsSection({
    attempt,
}: {
    attempt: AdventureAttempt;
    statLabels: Record<string, string>;
    skillLabels: Record<string, string>;
}) {
    const [isClaiming, setIsClaiming] = useState(false);

    // Handle claim rewards
    const handleClaim = () => {
        setIsClaiming(true);
        router.post(`/adventures/attempts/${attempt.id}/claim`, {}, {
            onFinish: () => setIsClaiming(false),
        });
    };

    return (
        <div className="space-y-6">
            {/* Roll Results - on a paper scrap */}
            <PaperCard rotation={-0.3} variant="torn">
                <h2 className="text-lg font-serif font-semibold text-amber-900 mb-4 flex items-center gap-2">
                    <span>🎲</span>
                    <span className="italic">Roll Results</span>
                </h2>

                <div className="flex items-center justify-center gap-8 mb-4">
                    <div className="text-center">
                        <div
                            className="text-4xl font-serif font-bold text-amber-900"
                            style={{ fontFamily: 'Georgia, serif' }}
                        >
                            {attempt.roll_result}
                        </div>
                        <div className="text-sm text-amber-800/60 font-serif italic">Your Roll</div>
                    </div>
                    <div className="text-2xl text-amber-800/40 font-serif">vs</div>
                    <div className="text-center">
                        <div
                            className="text-4xl font-serif font-bold text-amber-800/70"
                            style={{ fontFamily: 'Georgia, serif' }}
                        >
                            {attempt.success_chance}%
                        </div>
                        <div className="text-sm text-amber-800/60 font-serif italic">Success Chance</div>
                    </div>
                </div>

                {attempt.is_critical && (
                    <div className="text-center text-amber-700 font-serif font-medium animate-pulse">
                        ⭐ Critical Roll! (Doubles) - Double Rewards! ⭐
                    </div>
                )}
            </PaperCard>

            {/* Dialogue Section */}
            <AttemptDialogueSection attempt={attempt} />

            {/* Rewards - on aged parchment */}
            {attempt.rewards_granted && attempt.rewards_granted.length > 0 && (
                <PaperCard rotation={0.4} variant="aged">
                    <h2 className="text-lg font-serif font-semibold text-amber-900 mb-4 flex items-center gap-2">
                        <span>🎁</span>
                        <span className="italic">Rewards</span>
                    </h2>

                    <div className="space-y-3 mb-6">
                        {attempt.rewards_granted.map((reward, index) => (
                            <AttemptRewardItem key={index} reward={reward} />
                        ))}
                    </div>

                    {!attempt.rewards_claimed ? (
                        <button
                            onClick={handleClaim}
                            disabled={isClaiming}
                            className={`w-full py-3 rounded-lg font-serif font-medium transition-all ${
                                isClaiming
                                    ? 'bg-stone-400 text-stone-600 cursor-not-allowed'
                                    : 'bg-gradient-to-br from-amber-600 to-amber-800 hover:from-amber-500 hover:to-amber-700 text-amber-100 shadow-lg border border-amber-500/30'
                            }`}
                            style={!isClaiming ? {
                                boxShadow: '0 2px 8px rgba(139,90,43,0.4), inset 0 1px 0 rgba(255,255,255,0.1)',
                            } : undefined}
                        >
                            {isClaiming ? 'Claiming...' : '✨ Claim Rewards'}
                        </button>
                    ) : (
                        <div className="text-center text-green-700 py-3 font-serif">
                            ✓ Rewards Claimed
                        </div>
                    )}
                </PaperCard>
            )}
        </div>
    );
}

/**
 * AttemptDialogueSection - Displays adventure dialogue based on outcome with paper aesthetic.
 */
function AttemptDialogueSection({ attempt }: { attempt: AdventureAttempt }) {
    const isSuccess = attempt.outcome?.value === 'success' || attempt.outcome?.value === 'critical_success';
    const isPartial = attempt.outcome?.value === 'partial_success';

    // Determine which dialogue to show (partial success still shows success dialogue per spec)
    const dialogue = isSuccess || isPartial
        ? attempt.adventure?.dialogue_success
        : attempt.adventure?.dialogue_failure;

    if (!dialogue) return null;

    // Get wax seal colors based on outcome
    const getSealColors = (): { from: string; to: string; border: string } => {
        if (isSuccess) return { from: 'from-green-600', to: 'to-green-800', border: 'border-green-500/50' };
        if (isPartial) return { from: 'from-yellow-600', to: 'to-yellow-800', border: 'border-yellow-500/50' };
        return { from: 'from-red-700', to: 'to-red-900', border: 'border-red-600/50' };
    };

    const getIcon = (): string => {
        if (isSuccess) return '🎉';
        if (isPartial) return '😅';
        return '😔';
    };

    const getTitle = (): string => {
        if (isSuccess) return 'Victory!';
        if (isPartial) return 'Close Call...';
        return 'Defeat...';
    };

    const colors = getSealColors();

    return (
        <PaperCard rotation={isSuccess ? 0.6 : isPartial ? -0.4 : 0.2} variant="aged">
            <div className="relative">
                {/* Decorative quote icon */}
                <div className="absolute -top-2 -left-2 text-amber-800/20 text-4xl font-serif">"</div>

                <div className="flex items-start gap-4 relative z-10">
                    {/* Wax seal with outcome icon */}
                    <div
                        className={`w-14 h-14 rounded-full flex items-center justify-center bg-gradient-to-br ${colors.from} ${colors.to} border-2 ${colors.border} flex-shrink-0`}
                        style={{
                            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3), 0 2px 4px rgba(0,0,0,0.2)',
                        }}
                    >
                        <span className="text-2xl">{getIcon()}</span>
                    </div>

                    <div className="flex-1">
                        <h3 className="text-lg font-serif font-semibold text-amber-900 mb-2 italic">
                            {getTitle()}
                        </h3>
                        {/* Render dialogue as markdown for proper formatting */}
                        <div className="text-amber-900/80 text-lg italic leading-relaxed font-serif prose prose-amber max-w-none">
                            <EditorRenderer content={dialogue} theme="parchment" />
                        </div>
                    </div>
                </div>

                {/* Closing quote */}
                <div className="absolute -bottom-4 -right-2 text-amber-800/20 text-4xl font-serif rotate-180">"</div>
            </div>
        </PaperCard>
    );
}

/**
 * AttemptRewardItem - Displays a single reward with paper aesthetic.
 */
function AttemptRewardItem({ reward }: { reward: GrantedReward }) {
    if (!reward) return null;

    return (
        <div
            className="flex items-center justify-between rounded-lg p-3 border border-amber-800/20"
            style={{
                background: 'linear-gradient(135deg, rgba(245,230,211,0.6), rgba(232,213,183,0.6))',
            }}
        >
            <div className="flex items-center gap-3">
                {reward.type === 'item' && (
                    <>
                        <span className="text-xl">🎁</span>
                        <div>
                            <div className="text-amber-900 font-serif font-medium">{reward.item_name || 'Item'}</div>
                            {reward.quality !== undefined && (
                                <div className="text-xs text-amber-800/60 font-serif italic">Quality: {reward.quality}%</div>
                            )}
                        </div>
                    </>
                )}
                {reward.type === 'material' && (
                    <>
                        <span className="text-xl">📦</span>
                        <div>
                            <div className="text-amber-900 font-serif font-medium">{reward.material_type}</div>
                            <div className="text-xs text-amber-800/60 font-serif italic">x{reward.quantity}</div>
                        </div>
                    </>
                )}
                {reward.type === 'xp' && (
                    <>
                        <span className="text-xl">⭐</span>
                        <div>
                            <div className="text-green-800 font-serif font-medium">{reward.total_xp} XP</div>
                            <div className="text-xs text-amber-800/60 font-serif italic">
                                {reward.xp_per_character} per character
                            </div>
                        </div>
                    </>
                )}
                {reward.type === 'blueprint' && (
                    <>
                        <span className="text-xl">📜</span>
                        <div>
                            <div className="text-cyan-800 font-serif font-medium">{reward.item_name || 'Blueprint'}</div>
                            {reward.quality !== undefined && (
                                <div className="text-xs text-amber-800/60 font-serif italic">Quality: {reward.quality}%</div>
                            )}
                        </div>
                    </>
                )}
                {reward.type === 'reputation' && (
                    <>
                        <span className="text-xl">🏛️</span>
                        <div>
                            <div className="text-blue-800 font-serif font-medium">{reward.amount >= 0 ? '+' : ''}{reward.amount} Reputation</div>
                            <div className="text-xs text-amber-800/60 font-serif italic">
                                {reward.enclave_name}{reward.new_level ? ` → ${reward.new_level}` : ''}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

/**
 * Format ISO date string to readable format for attempt details.
 */
function formatAttemptDate(isoString: string): string {
    const date = new Date(isoString);
    return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

/**
 * AdventureDetailContent - Displays adventure details with character selection within the hex layout.
 * This is a port of the Adventures/Show page content to work within the hex view tabs.
 */
function AdventureDetailContent({
    adventure,
    characters,
    hexId,
    statLabels,
    skillLabels,
}: {
    adventure: Adventure;
    characters: AvailableCharacter[];
    hexId: number;
    statLabels: Record<string, string>;
    skillLabels: Record<string, string>;
}) {
    const [selectedCharacterIds, setSelectedCharacterIds] = useState<number[]>([]);
    const [successChance, setSuccessChance] = useState<number | null>(null);
    const [isCalculating, setIsCalculating] = useState(false);
    const [isStarting, setIsStarting] = useState(false);

    // Toggle character selection
    const toggleCharacter = (characterId: number) => {
        setSelectedCharacterIds((prev) =>
            prev.includes(characterId)
                ? prev.filter((id) => id !== characterId)
                : [...prev, characterId]
        );
    };

    // Calculate success chance when selection changes
    const calculateSuccessChance = useCallback(async () => {
        if (selectedCharacterIds.length === 0) {
            setSuccessChance(null);
            return;
        }

        setIsCalculating(true);
        try {
            const response = await fetch(`/adventures/${adventure.id}/calculate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
                },
                body: JSON.stringify({ character_ids: selectedCharacterIds }),
            });
            const data = await response.json();
            setSuccessChance(data.success_chance);
        } catch (error) {
            console.error('Failed to calculate success chance:', error);
        } finally {
            setIsCalculating(false);
        }
    }, [adventure.id, selectedCharacterIds]);

    // Recalculate when selection changes
    useEffect(() => {
        calculateSuccessChance();
    }, [calculateSuccessChance]);

    // Start the adventure
    const handleStart = () => {
        if (selectedCharacterIds.length === 0) return;

        setIsStarting(true);
        router.post(`/adventures/${adventure.id}/start`, {
            character_ids: selectedCharacterIds,
            hex_id: hexId,
        }, {
            onFinish: () => setIsStarting(false),
        });
    };

    // Get challenge label
    const getChallengeLabel = (statType: string): string => {
        return statLabels[statType] || skillLabels[statType] || statType;
    };

    // Get color class based on success chance
    const getSuccessColor = (chance: number): string => {
        if (chance >= 70) return 'text-green-400';
        if (chance >= 50) return 'text-yellow-400';
        if (chance >= 30) return 'text-orange-400';
        return 'text-red-400';
    };

    // Get unique stat types from challenges for display (including stage challenges for multi-stage)
    const relevantStatTypes = adventure.is_multi_stage && adventure.stages
        ? [...new Set(adventure.stages.flatMap(s => s.challenges.map(c => c.stat_type)))]
        : [...new Set(adventure.challenges.map((c) => c.stat_type))];

    // Filter guaranteed rewards (for single-stage, or all stage rewards for multi-stage)
    const guaranteedRewards = adventure.is_multi_stage && adventure.stages
        ? adventure.stages.flatMap(s => s.rewards.filter(r => r.chance_percent === 100))
        : adventure.rewards.filter((r) => r.chance_percent === 100);

    // Calculate total duration for multi-stage adventures (10 min per stage)
    const estimatedMinutes = adventure.is_multi_stage
        ? (adventure.stage_count || 1) * 10
        : (adventure.estimated_minutes || 0);

    return (
        <div className="space-y-6 max-w-2xl mx-auto">
            {/* Back to Adventures Link */}
            <button
                onClick={() => router.visit(`/hex/${hexId}?tab=adventures`)}
                className="inline-flex items-center text-amber-400 hover:text-amber-300 transition-colors font-serif"
            >
                ← Back to Adventures
            </button>

            {/* ============================================ */}
            {/* PARCHMENT SECTION - Roleplay/Narrative Content */}
            {/* ============================================ */}
            <PaperCard variant="aged">
                {/* Adventure Title */}
                <h1 className="text-3xl font-serif text-amber-900 tracking-wide mb-4 text-center">
                    {adventure.name}
                </h1>

                {/* Decorative divider */}
                <div className="flex items-center justify-center gap-4 mb-6">
                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-800/30 to-transparent" />
                    <span className="text-amber-800/40">❧</span>
                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-800/30 to-transparent" />
                </div>

                {/* Description */}
                {adventure.description && (
                    <div className="text-amber-900/80 mb-6 font-serif leading-relaxed prose prose-amber max-w-none prose-headings:text-amber-900 prose-p:text-amber-900/80">
                        <EditorRenderer content={adventure.description} theme="parchment" />
                    </div>
                )}

                {/* The Story Begins - Dialogue (Single-stage only) */}
                {!adventure.is_multi_stage && adventure.dialogue_start && (
                    <div className="relative mt-6 pt-6 border-t border-amber-800/20">
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#e8d5b7] px-4">
                            <span className="text-amber-800/60 font-serif italic text-sm">The Story Begins...</span>
                        </div>

                        <div className="relative">
                            <div className="absolute -top-2 -left-2 text-amber-800/20 text-4xl font-serif">"</div>
                            <div className="relative z-10 text-amber-900/80 leading-relaxed font-serif italic pl-6 pr-6">
                                <EditorRenderer content={adventure.dialogue_start} theme="parchment" />
                            </div>
                            <div className="absolute -bottom-2 -right-2 text-amber-800/20 text-4xl font-serif rotate-180">"</div>
                        </div>
                    </div>
                )}

                {/* Multi-stage indicator */}
                {adventure.is_multi_stage && (
                    <div className="relative mt-6 pt-6 border-t border-amber-800/20">
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#e8d5b7] px-4">
                            <span className="text-purple-800/80 font-serif italic text-sm">A Multi-Stage Quest</span>
                        </div>
                        <p className="text-amber-900/70 font-serif text-center italic">
                            This adventure unfolds across {adventure.stage_count} stages. Your party must face each challenge
                            in turn, with healing opportunities between stages. Take heed — failure at any stage may
                            end your quest early.
                        </p>
                    </div>
                )}
            </PaperCard>

            {/* ============================================ */}
            {/* OFFICIAL FORM - Fantasy Guild Document Style */}
            {/* ============================================ */}
            <div
                className="relative rounded-lg overflow-hidden"
                style={{
                    background: 'linear-gradient(135deg, #faf8f0 0%, #f5f0e1 50%, #faf8f0 100%)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15), inset 0 0 60px rgba(139, 90, 43, 0.05)',
                    border: '2px solid #c9a961',
                }}
            >
                {/* Decorative corner flourishes */}
                <div className="absolute top-2 left-2 text-amber-700/20 text-2xl">❦</div>
                <div className="absolute top-2 right-2 text-amber-700/20 text-2xl rotate-90">❦</div>
                <div className="absolute bottom-2 left-2 text-amber-700/20 text-2xl -rotate-90">❦</div>
                <div className="absolute bottom-2 right-2 text-amber-700/20 text-2xl rotate-180">❦</div>

                {/* Form Header */}
                <div
                    className="px-6 py-4 text-center border-b-2"
                    style={{
                        background: 'linear-gradient(180deg, rgba(139, 90, 43, 0.15) 0%, rgba(139, 90, 43, 0.05) 100%)',
                        borderColor: '#c9a961',
                    }}
                >
                    <div className="text-xs font-serif text-amber-800/60 tracking-widest uppercase mb-1">
                        Adventurer's Guild Official Document
                    </div>
                    <h2 className="text-xl font-serif font-bold text-amber-900 tracking-wide">
                        Quest Dispatch Authorization
                    </h2>
                    <div className="text-sm font-serif text-amber-800/70 mt-1">
                        Form #QDF-{adventure.id.toString().padStart(4, '0')}
                    </div>
                </div>

                {/* Form Body */}
                <div className="p-6 space-y-6">
                    {/* Quest Parameters Section */}
                    <div className="border-2 border-amber-800/20 rounded overflow-hidden bg-white/50">
                        <div className="bg-amber-100/50 px-4 py-2 border-b border-amber-800/20">
                            <h3 className="font-serif font-semibold text-amber-900 text-sm uppercase tracking-wide">
                                § Quest Parameters
                            </h3>
                        </div>
                        <div className="p-4 grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-serif text-amber-800/70 uppercase tracking-wide mb-1">
                                    Difficulty Rating
                                </label>
                                <div className="text-lg font-serif font-bold text-amber-900">
                                    {adventure.total_difficulty}
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-serif text-amber-800/70 uppercase tracking-wide mb-1">
                                    Est. Duration
                                </label>
                                <div className="text-lg font-serif font-bold text-amber-900">
                                    {formatDuration(estimatedMinutes)}
                                </div>
                            </div>
                            {adventure.is_multi_stage && (
                            <div>
                                <label className="block text-xs font-serif text-amber-800/70 uppercase tracking-wide mb-1">
                                    Quest Type
                                </label>
                                <div className="text-lg font-serif font-bold text-purple-800">
                                    Multi-Stage ({adventure.stage_count} stages)
                                </div>
                            </div>
                            )}
                        </div>
                    </div>

                    {/* Skill Requirements Section - Single Stage */}
                    {!adventure.is_multi_stage && adventure.challenges.length > 0 && (
                        <div className="border-2 border-amber-800/20 rounded overflow-hidden bg-white/50">
                            <div className="bg-amber-100/50 px-4 py-2 border-b border-amber-800/20">
                                <h3 className="font-serif font-semibold text-amber-900 text-sm uppercase tracking-wide">
                                    § Required Skills Assessment
                                </h3>
                            </div>
                            <div className="p-4">
                                <div className="grid gap-2 sm:grid-cols-2">
                                    {adventure.challenges.map((challenge) => (
                                        <div
                                            key={challenge.id}
                                            className="flex items-center justify-between bg-amber-50/50 border border-amber-800/20 rounded px-3 py-2"
                                        >
                                            <span className="font-serif text-amber-900">{getChallengeLabel(challenge.stat_type)}</span>
                                            <span className={`font-serif font-bold ${
                                                challenge.difficulty >= 50 ? 'text-red-700' :
                                                challenge.difficulty >= 30 ? 'text-orange-700' :
                                                challenge.difficulty >= 15 ? 'text-amber-700' :
                                                'text-green-700'
                                            }`}>
                                                DC {challenge.difficulty}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Multi-Stage: Stage Breakdown */}
                    {adventure.is_multi_stage && adventure.stages && adventure.stages.length > 0 && (
                        <div className="border-2 border-amber-800/20 rounded overflow-hidden bg-white/50">
                            <div className="bg-amber-100/50 px-4 py-2 border-b border-amber-800/20">
                                <h3 className="font-serif font-semibold text-amber-900 text-sm uppercase tracking-wide">
                                    § Stage Breakdown
                                </h3>
                            </div>
                            <div className="p-4 space-y-4">
                                <p className="font-serif text-amber-800/70 text-sm italic">
                                    This quest consists of {adventure.stage_count} stages. Each stage must be completed in sequence.
                                    Characters take damage based on performance at each stage.
                                </p>
                                {adventure.stages.map((stage) => (
                                    <div key={stage.id} className="border border-amber-800/20 rounded overflow-hidden">
                                        <div className="bg-purple-100/50 px-4 py-2 border-b border-amber-800/20 flex items-center gap-3">
                                            <span className="bg-purple-700 text-white text-xs px-2 py-0.5 rounded font-serif">
                                                Stage {stage.stage_number}
                                            </span>
                                            <span className="font-serif text-amber-800/70 text-sm">
                                                {stage.challenges.length} challenge{stage.challenges.length !== 1 ? 's' : ''},
                                                {' '}{stage.rewards.length} reward{stage.rewards.length !== 1 ? 's' : ''}
                                            </span>
                                        </div>
                                        <div className="p-3 space-y-3">
                                            {/* Stage Dialogue Preview - Only show for Stage 1 */}
                                            {stage.stage_number === 1 && stage.dialogue_pre && (
                                                <div className="text-amber-900/70 font-serif text-sm italic border-l-2 border-amber-800/20 pl-3">
                                                    <EditorRenderer content={stage.dialogue_pre} theme="parchment" />
                                                </div>
                                            )}

                                            {/* Hidden dialogue indicator for stages 2+ */}
                                            {stage.stage_number > 1 && (
                                                <p className="font-serif text-amber-800/50 text-xs italic">
                                                    Dialogue revealed upon reaching this stage...
                                                </p>
                                            )}

                                            {/* Stage Challenges */}
                                            {stage.challenges.length > 0 && (
                                                <div className="flex flex-wrap gap-2">
                                                    {stage.challenges.map((challenge) => (
                                                        <div
                                                            key={challenge.id}
                                                            className="flex items-center gap-2 bg-amber-50/50 border border-amber-800/20 rounded px-2 py-1"
                                                        >
                                                            <span className="font-serif text-amber-900 text-sm">{getChallengeLabel(challenge.stat_type)}</span>
                                                            <span className={`font-serif font-bold text-sm ${
                                                                challenge.difficulty >= 50 ? 'text-red-700' :
                                                                challenge.difficulty >= 30 ? 'text-orange-700' :
                                                                challenge.difficulty >= 15 ? 'text-amber-700' :
                                                                'text-green-700'
                                                            }`}>
                                                                DC {challenge.difficulty}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Stage Rewards Preview */}
                                            {stage.rewards.length > 0 && (
                                                <div className="flex flex-wrap gap-2">
                                                    {stage.rewards.map((reward) => (
                                                        <div
                                                            key={reward.id}
                                                            className="flex items-center gap-1 bg-green-50/50 border border-green-800/20 rounded px-2 py-1"
                                                        >
                                                            <span className="text-sm">{reward.reward_type.icon}</span>
                                                            <span className="font-serif text-green-900 text-sm">
                                                                {reward.reward_type.value === 'item' && (reward.item_recipe?.name || 'Item')}
                                                                {reward.reward_type.value === 'blueprint' && (reward.item_recipe?.name || 'Blueprint')}
                                                                {reward.reward_type.value === 'material' && `${reward.material_type}`}
                                                                {reward.reward_type.value === 'xp' && 'XP'}
                                                                {reward.reward_type.value === 'reputation' && 'Rep'}
                                                            </span>
                                                            {reward.chance_percent < 100 && (
                                                                <span className="text-xs text-amber-700">({reward.chance_percent}%)</span>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Compensation Section */}
                    {guaranteedRewards.length > 0 && (
                        <div className="border-2 border-amber-800/20 rounded overflow-hidden bg-white/50">
                            <div className="bg-amber-100/50 px-4 py-2 border-b border-amber-800/20">
                                <h3 className="font-serif font-semibold text-amber-900 text-sm uppercase tracking-wide">
                                    § Guaranteed Compensation
                                </h3>
                            </div>
                            <div className="p-4">
                                <div className="space-y-2">
                                    {guaranteedRewards.map((reward) => (
                                        <div
                                            key={reward.id}
                                            className="flex items-center gap-3 bg-amber-50/50 border border-amber-800/20 rounded px-3 py-2"
                                        >
                                            <span className="text-lg">{reward.reward_type.icon}</span>
                                            <span className="font-serif text-amber-900">
                                                {reward.reward_type.value === 'item' && (reward.item_recipe?.name || 'Item')}
                                                {reward.reward_type.value === 'blueprint' && (reward.item_recipe?.name || 'Blueprint')}
                                                {reward.reward_type.value === 'material' && `${reward.quantity_min === reward.quantity_max ? reward.quantity_min : `${reward.quantity_min}-${reward.quantity_max}`}x ${reward.material_type}`}
                                                {reward.reward_type.value === 'xp' && `${reward.quantity_min === reward.quantity_max ? reward.quantity_min : `${reward.quantity_min}-${reward.quantity_max}`} XP`}
                                                {reward.reward_type.value === 'reputation' && `${reward.reputation_min === reward.reputation_max ? `+${reward.reputation_min}` : `+${reward.reputation_min}-${reward.reputation_max}`} Reputation with ${reward.enclave?.owner_name || reward.enclave?.name || 'Unknown'}`}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Assigned Personnel Section */}
                    <div className="border-2 border-amber-800/20 rounded overflow-hidden bg-white/50">
                        <div className="bg-amber-100/50 px-4 py-2 border-b border-amber-800/20">
                            <h3 className="font-serif font-semibold text-amber-900 text-sm uppercase tracking-wide">
                                § Assigned Personnel
                            </h3>
                        </div>
                        <div className="p-4">
                            {characters.length === 0 ? (
                                <div className="text-center py-4">
                                    <p className="font-serif text-amber-800/70 italic">No personnel available for assignment.</p>
                                    <button
                                        onClick={() => router.visit(`/hex/${hexId}?tab=attempts`)}
                                        className="mt-2 font-serif text-amber-700 hover:text-amber-900 underline text-sm"
                                    >
                                        View active deployments →
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <p className="font-serif text-amber-800/70 text-sm mb-4 italic">
                                        Select personnel to assign to this quest. Multiple assignees increase success probability.
                                    </p>
                                    <div className="grid gap-4 sm:grid-cols-2">
                                        {characters.map((character) => (
                                            <CharacterStampCard
                                                key={character.id}
                                                character={character}
                                                isSelected={selectedCharacterIds.includes(character.id)}
                                                onToggle={() => toggleCharacter(character.id)}
                                                relevantStatTypes={relevantStatTypes}
                                                statLabels={statLabels}
                                                skillLabels={skillLabels}
                                            />
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Authorization Section - Stamp Style */}
                    <div className="border-t-2 border-amber-800/30 pt-6">
                        <div className="flex items-center justify-between gap-6">
                            <div className="space-y-1 flex-1">
                                {selectedCharacterIds.length === 0 ? (
                                    <p className="font-serif text-amber-800/60 italic">
                                        Select at least one character to authorize dispatch
                                    </p>
                                ) : (
                                    <>
                                        <p className="font-serif text-amber-900 font-medium">
                                            {selectedCharacterIds.length} personnel assigned
                                        </p>
                                        {isCalculating ? (
                                            <p className="font-serif text-amber-800/70 text-sm italic">
                                                Calculating success probability...
                                            </p>
                                        ) : successChance !== null && (
                                            <p className={`font-serif text-sm font-semibold ${
                                                successChance >= 70 ? 'text-green-700' :
                                                successChance >= 50 ? 'text-amber-700' :
                                                successChance >= 30 ? 'text-orange-700' :
                                                'text-red-700'
                                            }`}>
                                                Estimated Success Rate: {successChance}%
                                            </p>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* Authorization Stamp Button */}
                            <div
                                className={`relative group ${selectedCharacterIds.length === 0 || isStarting ? 'opacity-50' : 'cursor-pointer'}`}
                                onClick={() => selectedCharacterIds.length > 0 && !isStarting && handleStart()}
                            >
                                <div
                                    className={`relative p-4 rounded-lg border-4 border-dashed transition-all duration-200 ${
                                        selectedCharacterIds.length > 0 && !isStarting
                                            ? 'group-hover:translate-y-0.5 group-hover:translate-x-0.5'
                                            : ''
                                    }`}
                                    style={{
                                        borderColor: selectedCharacterIds.length === 0 ? 'rgba(120, 100, 80, 0.3)' : 'rgba(22, 101, 52, 0.6)',
                                        background: selectedCharacterIds.length === 0
                                            ? 'linear-gradient(135deg, rgba(120, 100, 80, 0.08) 0%, rgba(120, 100, 80, 0.15) 100%)'
                                            : 'linear-gradient(135deg, rgba(22, 101, 52, 0.08) 0%, rgba(22, 101, 52, 0.15) 100%)',
                                    }}
                                >
                                    {/* Stamp ring effect */}
                                    <div className={`absolute inset-2 rounded-lg border-2 ${
                                        selectedCharacterIds.length === 0 ? 'border-amber-800/20' : 'border-green-800/30'
                                    }`} />

                                    <div className="relative text-center px-4">
                                        <div className={`text-lg font-serif font-bold tracking-wider uppercase mb-1 ${
                                            selectedCharacterIds.length === 0 ? 'text-amber-800/50' : 'text-green-900'
                                        }`}>
                                            {isStarting ? 'Processing...' : 'Authorized'}
                                        </div>
                                        <div className={`text-xs font-serif ${
                                            selectedCharacterIds.length === 0 ? 'text-amber-800/40' : 'text-green-800'
                                        }`}>
                                            DISPATCH ORDER
                                        </div>
                                    </div>
                                </div>
                                {/* Stamp shadow on hover */}
                                {selectedCharacterIds.length > 0 && !isStarting && (
                                    <div className="absolute inset-0 rounded-lg bg-green-900/10 opacity-0 group-hover:opacity-100 transition-opacity -z-10 translate-y-1 translate-x-1" />
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Form Footer */}
                <div
                    className="px-6 py-3 text-xs font-serif text-amber-800/50 text-center italic border-t-2"
                    style={{ borderColor: '#c9a961', background: 'rgba(139, 90, 43, 0.05)' }}
                >
                    This document is property of the Adventurer's Guild. Unauthorized duplication is prohibited.
                </div>
            </div>
        </div>
    );
}

/**
 * CharacterStampCard - Stamp-styled character selection card.
 */
function CharacterStampCard({
    character,
    isSelected,
    onToggle,
    relevantStatTypes,
    statLabels,
    skillLabels,
}: {
    character: AvailableCharacter;
    isSelected: boolean;
    onToggle: () => void;
    relevantStatTypes: string[];
    statLabels: Record<string, string>;
    skillLabels: Record<string, string>;
}) {
    const getStatValue = (statType: string): number => {
        if (character.stats && character.stats[statType] !== undefined) return character.stats[statType];
        if (character.skills && character.skills[statType] !== undefined) return character.skills[statType];
        if (character.equipment_bonuses && character.equipment_bonuses[statType] !== undefined) return character.equipment_bonuses[statType];
        return 0;
    };

    const getStatLabel = (statType: string): string => {
        return statLabels[statType] || skillLabels[statType] || statType.charAt(0).toUpperCase() + statType.slice(1).replace(/_/g, ' ');
    };

    const getStatColor = (value: number): string => {
        if (value >= 15) return 'text-green-800';
        if (value >= 10) return 'text-amber-800';
        if (value >= 5) return 'text-orange-800';
        return 'text-red-800';
    };

    return (
        <div
            className={`relative group cursor-pointer ${isSelected ? '' : 'opacity-60 hover:opacity-100'}`}
            onClick={onToggle}
        >
            <div
                className={`relative p-4 rounded-lg border-4 border-dashed transition-all duration-200 ${
                    isSelected ? 'group-hover:translate-y-0.5 group-hover:translate-x-0.5' : ''
                }`}
                style={{
                    borderColor: isSelected ? 'rgba(22, 101, 52, 0.6)' : 'rgba(120, 100, 80, 0.4)',
                    background: isSelected
                        ? 'linear-gradient(135deg, rgba(22, 101, 52, 0.08) 0%, rgba(22, 101, 52, 0.15) 100%)'
                        : 'linear-gradient(135deg, rgba(120, 100, 80, 0.05) 0%, rgba(120, 100, 80, 0.10) 100%)',
                }}
            >
                {/* Stamp ring effect */}
                <div className={`absolute inset-2 rounded-lg border-2 ${
                    isSelected ? 'border-green-800/30' : 'border-amber-800/20'
                }`} />

                <div className="relative text-center">
                    {/* Character Name */}
                    <div className={`text-base font-serif font-bold tracking-wide uppercase mb-0.5 ${
                        isSelected ? 'text-green-900' : 'text-amber-900'
                    }`}>
                        {character.name}
                    </div>

                    {/* Class & Level */}
                    <div className={`text-xs font-serif mb-2 ${
                        isSelected ? 'text-green-800' : 'text-amber-800/70'
                    }`}>
                        <span className="capitalize">{character.class}</span> • Lvl {character.level}
                    </div>

                    {/* Selected Checkmark or Assignment Status */}
                    <div className={`text-xs font-serif uppercase tracking-wider mb-2 ${
                        isSelected ? 'text-green-800 font-bold' : 'text-amber-800/50'
                    }`}>
                        {isSelected ? '✓ ASSIGNED' : 'AVAILABLE'}
                    </div>

                    {/* Relevant Stats */}
                    {relevantStatTypes.length > 0 && (
                        <div className="flex flex-wrap justify-center gap-2 pt-2 border-t border-amber-800/20">
                            {relevantStatTypes.map((statType) => {
                                const value = getStatValue(statType);
                                return (
                                    <div
                                        key={statType}
                                        className="text-xs font-serif"
                                    >
                                        <span className={isSelected ? 'text-green-800/70' : 'text-amber-800/60'}>
                                            {getStatLabel(statType)}:
                                        </span>{' '}
                                        <span className={`font-bold ${getStatColor(value)}`}>{value}</span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Stamp shadow on hover when selected */}
            {isSelected && (
                <div className="absolute inset-0 rounded-lg bg-green-900/10 opacity-0 group-hover:opacity-100 transition-opacity -z-10 translate-y-1 translate-x-1" />
            )}
        </div>
    );
}

/**
 * AdventureChallengeCard - Displays a single challenge in the adventure detail view with paper aesthetic.
 */
function AdventureChallengeCard({ challenge, label }: { challenge: { stat_type: string; difficulty: number }; label: string }) {
    const getDifficultyColor = (difficulty: number): string => {
        if (difficulty >= 50) return 'text-red-700';
        if (difficulty >= 30) return 'text-orange-700';
        if (difficulty >= 15) return 'text-yellow-700';
        return 'text-green-700';
    };

    return (
        <div
            className="rounded-lg p-3 flex items-center justify-between border border-amber-800/20"
            style={{
                background: 'linear-gradient(135deg, rgba(245,230,211,0.6), rgba(232,213,183,0.6))',
            }}
        >
            <span className="text-amber-900 font-serif">{label}</span>
            <span className={`font-serif font-medium ${getDifficultyColor(challenge.difficulty)}`}>
                {challenge.difficulty}
            </span>
        </div>
    );
}

/**
 * AdventureRewardsSection - Displays guaranteed rewards (100% chance only) in adventure detail with paper aesthetic.
 */
function AdventureRewardsSection({ adventure }: { adventure: Adventure }) {
    // Filter to only show 100% guaranteed rewards
    const guaranteedRewards = adventure.rewards.filter((r) => r.chance_percent === 100);

    if (guaranteedRewards.length === 0) {
        return null;
    }

    return (
        <PaperCard>
            <h2 className="text-lg font-serif font-semibold text-amber-900 mb-4 flex items-center gap-2">
                <span>🎁</span>
                <span className="italic">Guaranteed Rewards</span>
            </h2>
            <div className="grid gap-2">
                {guaranteedRewards.map((reward) => (
                    <div
                        key={reward.id}
                        className="flex items-center justify-between rounded-lg p-3 border border-amber-800/20"
                        style={{
                            background: 'linear-gradient(135deg, rgba(245,230,211,0.6), rgba(232,213,183,0.6))',
                        }}
                    >
                        <div className="flex items-center gap-2 font-serif">
                            {/* Display reward type icon */}
                            <span>{reward.reward_type.icon}</span>

                            {/* Item rewards */}
                            {reward.reward_type.value === 'item' && (
                                <>
                                    <span className="text-purple-800">{reward.item_recipe?.name || 'Item'}</span>
                                    {reward.item_rarity && (
                                        <span className="text-xs text-amber-800/60 italic">({reward.item_rarity.label})</span>
                                    )}
                                </>
                            )}

                            {/* Material rewards */}
                            {reward.reward_type.value === 'material' && (
                                <>
                                    <span className="text-amber-900">{reward.material_type}</span>
                                    <span className="text-amber-800/60 text-sm italic">
                                        x{reward.quantity_min === reward.quantity_max
                                            ? reward.quantity_min
                                            : `${reward.quantity_min}-${reward.quantity_max}`}
                                    </span>
                                </>
                            )}

                            {/* XP rewards */}
                            {reward.reward_type.value === 'xp' && (
                                <span className="text-green-800">
                                    {reward.quantity_min === reward.quantity_max
                                        ? `${reward.quantity_min} XP`
                                        : `${reward.quantity_min}-${reward.quantity_max} XP`}
                                </span>
                            )}

                            {/* Blueprint rewards */}
                            {reward.reward_type.value === 'blueprint' && (
                                <>
                                    <span className="text-cyan-800">{reward.item_recipe?.name || 'Blueprint'}</span>
                                    {reward.item_rarity && (
                                        <span className="text-xs text-amber-800/60 italic">({reward.item_rarity.label})</span>
                                    )}
                                </>
                            )}

                            {/* Reputation rewards */}
                            {reward.reward_type.value === 'reputation' && (
                                <>
                                    <span className="text-blue-800">
                                        {reward.reputation_min === reward.reputation_max
                                            ? `+${reward.reputation_min}`
                                            : `+${reward.reputation_min}-${reward.reputation_max}`} Reputation
                                    </span>
                                    <span className="text-amber-800/60 text-sm italic">
                                        with {reward.enclave?.owner_name || reward.enclave?.name || 'Unknown'}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </PaperCard>
    );
}

/**
 * AdventureCharacterCard - Selectable character card for adventure detail view.
 */
function AdventureCharacterCard({
    character,
    isSelected,
    onToggle,
    relevantStatTypes,
    statLabels,
    skillLabels,
}: {
    character: AvailableCharacter;
    isSelected: boolean;
    onToggle: () => void;
    relevantStatTypes: string[];
    statLabels: Record<string, string>;
    skillLabels: Record<string, string>;
}) {
    /**
     * Get the character's value for a given stat type.
     * Checks stats first, then skills, then equipment bonuses.
     */
    const getStatValue = (statType: string): number => {
        // Check base stats
        if (character.stats && character.stats[statType] !== undefined) {
            return character.stats[statType];
        }
        // Check skills
        if (character.skills && character.skills[statType] !== undefined) {
            return character.skills[statType];
        }
        // Check equipment bonuses
        if (character.equipment_bonuses && character.equipment_bonuses[statType] !== undefined) {
            return character.equipment_bonuses[statType];
        }
        return 0;
    };

    /**
     * Get the display label for a stat type.
     */
    const getStatLabel = (statType: string): string => {
        // Check stat labels first, then skill labels
        if (statLabels[statType]) {
            return statLabels[statType];
        }
        if (skillLabels[statType]) {
            return skillLabels[statType];
        }
        // Fallback: capitalize the stat type
        return statType.charAt(0).toUpperCase() + statType.slice(1).replace(/_/g, ' ');
    };

    /**
     * Get color class based on stat value.
     */
    const getStatColor = (value: number): string => {
        if (value >= 15) return 'text-green-700';
        if (value >= 10) return 'text-yellow-700';
        if (value >= 5) return 'text-orange-700';
        return 'text-red-700';
    };

    return (
        <button
            onClick={onToggle}
            className={`text-left p-4 rounded-lg border-2 transition-all ${
                isSelected
                    ? 'border-amber-600 shadow-md'
                    : 'border-amber-800/30 hover:border-amber-700/50'
            }`}
            style={{
                background: isSelected
                    ? 'linear-gradient(135deg, #e5d4b8 0%, #d9c9a5 50%, #e0d2bc 100%)'
                    : 'linear-gradient(135deg, rgba(245,230,211,0.6), rgba(232,213,183,0.6))',
            }}
        >
            {/* Header: Name and checkbox */}
            <div className="flex items-center justify-between mb-2">
                <span className="font-serif font-medium text-amber-900">{character.name}</span>
                <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    isSelected ? 'bg-amber-700 border-amber-700' : 'border-amber-800/40'
                }`}>
                    {isSelected && <span className="text-white text-xs">✓</span>}
                </span>
            </div>

            {/* Class and Level */}
            <div className="text-sm text-amber-800/60 mb-3 font-serif italic">
                <span className="capitalize">{character.class}</span>
                <span className="mx-1">•</span>
                <span>Level {character.level}</span>
            </div>

            {/* Relevant Stats for this Adventure */}
            {relevantStatTypes.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-2 border-t border-amber-800/20">
                    {relevantStatTypes.map((statType) => {
                        const value = getStatValue(statType);
                        return (
                            <div
                                key={statType}
                                className="flex items-center gap-1 text-xs px-2 py-1 rounded border border-amber-800/20 font-serif"
                                style={{
                                    background: 'rgba(245,230,211,0.5)',
                                }}
                                title={`${getStatLabel(statType)}: ${value}`}
                            >
                                <span className="text-amber-800/60">{getStatLabel(statType)}:</span>
                                <span className={`font-medium ${getStatColor(value)}`}>{value}</span>
                            </div>
                        );
                    })}
                </div>
            )}
        </button>
    );
}

/**
 * Decorative corner flourish SVG for parchment borders.
 */
function ReputationCornerFlourish({ className = '' }: { className?: string }) {
    return (
        <svg
            className={`w-10 h-10 text-amber-800/30 ${className}`}
            viewBox="0 0 100 100"
            fill="currentColor"
        >
            <path d="M0,0 Q50,10 50,50 Q10,50 0,0 M10,0 Q40,20 40,40 Q20,40 10,0" />
        </svg>
    );
}

/**
 * Wax seal component for visual flair.
 */
function ReputationWaxSeal() {
    return (
        <div
            className="w-10 h-10 rounded-full flex items-center justify-center bg-gradient-to-br from-red-700 to-red-900"
            style={{
                boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3), 0 2px 4px rgba(0,0,0,0.3)',
            }}
        >
            <span className="text-sm font-serif text-amber-100/80">✦</span>
        </div>
    );
}

/**
 * ReputationTab - Displays the user's reputation with this enclave.
 * Shows current level, progress bar, and reputation value with parchment styling.
 */
function ReputationTab({
    reputation,
    hexName,
}: {
    reputation: ReputationData | null | undefined;
    hexName: string;
}) {
    if (!reputation) {
        return (
            <div className="max-w-2xl mx-auto">
                <div className="relative">
                    <div
                        className="relative rounded-lg overflow-hidden p-8"
                        style={{
                            background: 'linear-gradient(135deg, #f5e6d3 0%, #e8d5b7 25%, #f0e2cc 50%, #e5d4b8 75%, #f2e4c9 100%)',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.4), inset 0 0 60px rgba(139,90,43,0.15)',
                        }}
                    >
                        <ReputationCornerFlourish className="absolute top-2 left-2" />
                        <ReputationCornerFlourish className="absolute top-2 right-2 rotate-90" />
                        <ReputationCornerFlourish className="absolute bottom-2 left-2 -rotate-90" />
                        <ReputationCornerFlourish className="absolute bottom-2 right-2 rotate-180" />

                        <div className="text-center py-8">
                            <div className="text-6xl mb-4">🔒</div>
                            <h2 className="text-xl font-serif font-semibold text-amber-900 mb-2">Sign In Required</h2>
                            <p className="text-amber-800/70 font-serif">
                                You need to sign in to view your reputation with {hexName}.
                            </p>
                        </div>
                    </div>
                    <div
                        className="absolute -bottom-2 left-4 right-4 h-4 rounded-full"
                        style={{ background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.3) 0%, transparent 70%)' }}
                    />
                </div>
            </div>
        );
    }

    /**
     * Get color class based on reputation level (parchment-friendly colors).
     */
    const getLevelColor = (level: string): string => {
        switch (level) {
            case 'Exalted':
                return 'text-amber-700';
            case 'Revered':
                return 'text-purple-700';
            case 'Honored':
                return 'text-blue-700';
            case 'Friendly':
                return 'text-green-700';
            case 'Neutral':
                return 'text-amber-800';
            case 'Unfriendly':
                return 'text-orange-700';
            case 'Hostile':
                return 'text-red-700';
            case 'Hated':
                return 'text-red-900';
            default:
                return 'text-amber-800';
        }
    };

    /**
     * Get progress bar color based on reputation level.
     */
    const getProgressColor = (level: string): string => {
        switch (level) {
            case 'Exalted':
                return 'bg-gradient-to-r from-amber-500 to-amber-600';
            case 'Revered':
                return 'bg-gradient-to-r from-purple-500 to-purple-600';
            case 'Honored':
                return 'bg-gradient-to-r from-blue-500 to-blue-600';
            case 'Friendly':
                return 'bg-gradient-to-r from-green-500 to-green-600';
            case 'Neutral':
                return 'bg-gradient-to-r from-stone-400 to-stone-500';
            case 'Unfriendly':
                return 'bg-gradient-to-r from-orange-500 to-orange-600';
            case 'Hostile':
                return 'bg-gradient-to-r from-red-500 to-red-600';
            case 'Hated':
                return 'bg-gradient-to-r from-red-700 to-red-800';
            default:
                return 'bg-gradient-to-r from-stone-400 to-stone-500';
        }
    };

    /**
     * Get background color for current level highlight.
     */
    const getLevelBgColor = (level: string): string => {
        switch (level) {
            case 'Exalted':
                return 'bg-amber-100/70 border-amber-400/50';
            case 'Revered':
                return 'bg-purple-100/70 border-purple-400/50';
            case 'Honored':
                return 'bg-blue-100/70 border-blue-400/50';
            case 'Friendly':
                return 'bg-green-100/70 border-green-400/50';
            case 'Neutral':
                return 'bg-stone-100/70 border-stone-400/50';
            case 'Unfriendly':
                return 'bg-orange-100/70 border-orange-400/50';
            case 'Hostile':
                return 'bg-red-100/70 border-red-400/50';
            case 'Hated':
                return 'bg-red-200/70 border-red-500/50';
            default:
                return 'bg-stone-100/70 border-stone-400/50';
        }
    };

    return (
        <div className="max-w-2xl mx-auto">
            {/* Main parchment scroll */}
            <div className="relative">
                <div
                    className="relative rounded-lg overflow-hidden"
                    style={{
                        background: 'linear-gradient(135deg, #f5e6d3 0%, #e8d5b7 25%, #f0e2cc 50%, #e5d4b8 75%, #f2e4c9 100%)',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.4), inset 0 0 60px rgba(139,90,43,0.15)',
                    }}
                >
                    {/* Decorative corner flourishes */}
                    <ReputationCornerFlourish className="absolute top-2 left-2" />
                    <ReputationCornerFlourish className="absolute top-2 right-2 rotate-90" />
                    <ReputationCornerFlourish className="absolute bottom-2 left-2 -rotate-90" />
                    <ReputationCornerFlourish className="absolute bottom-2 right-2 rotate-180" />

                    {/* Aged paper edge effects */}
                    <div
                        className="absolute top-0 left-0 right-0 h-3"
                        style={{ background: 'linear-gradient(to bottom, rgba(139,90,43,0.2), transparent)' }}
                    />
                    <div
                        className="absolute bottom-0 left-0 right-0 h-3"
                        style={{ background: 'linear-gradient(to top, rgba(139,90,43,0.2), transparent)' }}
                    />

                    {/* Inner content area */}
                    <div className="relative p-6 pt-8">
                        {/* Ornate header */}
                        <div className="text-center mb-6">
                            <div className="inline-flex items-center gap-3 mb-2">
                                <ReputationWaxSeal />
                                <h1 className="text-2xl font-serif text-amber-900 tracking-wide">
                                    Standing with {reputation.enclaveName || hexName}
                                </h1>
                                <ReputationWaxSeal />
                            </div>
                            <p className="text-sm font-serif italic text-amber-800/60">
                                Your reputation affects your interactions and available opportunities
                            </p>
                        </div>

                        {/* Decorative divider */}
                        <div className="flex items-center justify-center gap-2 mb-6">
                            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-800/30 to-transparent" />
                            <span className="text-amber-800/40 text-lg">✦</span>
                            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-800/30 to-transparent" />
                        </div>

                        {/* Main reputation display */}
                        <div
                            className="p-6 rounded-lg mb-6"
                            style={{
                                background: 'linear-gradient(135deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.3) 100%)',
                                boxShadow: 'inset 0 1px 3px rgba(139,90,43,0.1)',
                            }}
                        >
                            {/* Level display */}
                            <div className="text-center mb-6">
                                <div className="text-6xl mb-3">
                                    {reputation.level === 'Exalted' && '👑'}
                                    {reputation.level === 'Revered' && '🌟'}
                                    {reputation.level === 'Honored' && '🏅'}
                                    {reputation.level === 'Friendly' && '🤝'}
                                    {reputation.level === 'Neutral' && '🔘'}
                                    {reputation.level === 'Unfriendly' && '😕'}
                                    {reputation.level === 'Hostile' && '⚠️'}
                                    {reputation.level === 'Hated' && '💀'}
                                </div>
                                <h2 className={`text-3xl font-serif font-bold ${getLevelColor(reputation.level)}`}>
                                    {reputation.level}
                                </h2>
                                <p className="text-amber-800/70 mt-2 font-serif">
                                    {reputation.value.toLocaleString()} reputation points
                                </p>
                            </div>

                            {/* Progress bar */}
                            <div>
                                <div className="flex justify-between text-sm text-amber-800/70 mb-2 font-serif">
                                    <span>Progress to next level</span>
                                    <span className="font-medium">{reputation.progress}%</span>
                                </div>
                                <div className="h-4 bg-amber-200/50 rounded-full overflow-hidden border border-amber-400/30">
                                    <div
                                        className={`h-full ${getProgressColor(reputation.level)} transition-all duration-300`}
                                        style={{ width: `${reputation.progress}%` }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Decorative divider */}
                        <div className="flex items-center justify-center gap-2 mb-6">
                            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-800/30 to-transparent" />
                            <span className="text-amber-800/40 text-sm">❧</span>
                            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-800/30 to-transparent" />
                        </div>

                        {/* Reputation levels guide */}
                        <div>
                            <h3 className="text-lg font-serif font-semibold text-amber-900 mb-4 text-center">
                                Reputation Ranks
                            </h3>
                            <div className="grid gap-2">
                                {[
                                    { level: 'Exalted', icon: '👑', desc: 'The highest honor' },
                                    { level: 'Revered', icon: '🌟', desc: 'Greatly respected' },
                                    { level: 'Honored', icon: '🏅', desc: 'Held in high regard' },
                                    { level: 'Friendly', icon: '🤝', desc: 'On good terms' },
                                    { level: 'Neutral', icon: '🔘', desc: 'Neither friend nor foe' },
                                    { level: 'Unfriendly', icon: '😕', desc: 'Distrusted' },
                                    { level: 'Hostile', icon: '⚠️', desc: 'Actively opposed' },
                                    { level: 'Hated', icon: '💀', desc: 'Despised enemy' },
                                ].map((tier) => (
                                    <div
                                        key={tier.level}
                                        className={`flex items-center gap-3 p-2.5 rounded-lg border transition-all ${
                                            tier.level === reputation.level
                                                ? getLevelBgColor(tier.level)
                                                : 'border-transparent hover:bg-white/30'
                                        }`}
                                    >
                                        <span className="text-xl">{tier.icon}</span>
                                        <div className="flex-1">
                                            <span className={`font-serif font-medium ${getLevelColor(tier.level)}`}>
                                                {tier.level}
                                            </span>
                                            <span className="text-amber-700/60 text-sm ml-2 font-serif italic">
                                                {tier.desc}
                                            </span>
                                        </div>
                                        {tier.level === reputation.level && (
                                            <span className="text-xs bg-amber-600/80 px-2 py-0.5 rounded font-serif text-white">
                                                Current
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Bottom flourish */}
                        <div className="flex items-center justify-center gap-2 mt-8">
                            <div className="h-px w-16 bg-gradient-to-r from-transparent to-amber-800/30" />
                            <span className="text-amber-800/30 text-sm">❧</span>
                            <div className="h-px w-16 bg-gradient-to-l from-transparent to-amber-800/30" />
                        </div>
                    </div>
                </div>

                {/* Shadow beneath the parchment */}
                <div
                    className="absolute -bottom-2 left-4 right-4 h-4 rounded-full"
                    style={{ background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.3) 0%, transparent 70%)' }}
                />
            </div>
        </div>
    );
}
